import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactECharts from 'echarts-for-react';
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  DollarSign,
  Factory,
  Filter,
  Gauge,
  Info,
  Layers3,
  Package,
  RefreshCw,
  Route,
  Search,
  ShieldCheck,
  Sparkles,
  Timer,
  X,
} from 'lucide-react';
import {
  redzoneDowntimeApi,
  RedzoneDynamicDashboardResponse,
  RedzoneDynamicDimension,
  RedzoneDynamicFacet,
  RedzoneDynamicFilters,
  RedzoneDynamicPayload,
  RedzoneDynamicRow,
  RedzonePlannedMode,
  RedzoneDowntimeArea,
  RedzoneDowntimeDashboardResponse,
  RedzoneDowntimeHierarchyLevel,
  RedzoneDowntimeLocation,
  RedzoneDowntimePayload,
  RedzoneDowntimeProblem,
  RedzoneDowntimeSkuOption,
} from '../services/redzoneDowntimeService';
import '../styles/redzoneDowntime.css';

const numberValue = (value: number | null | undefined): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : 0
);

const formatNumber = (value: number | null | undefined, digits = 1): string => (
  value === null || value === undefined
    ? '—'
    : value.toLocaleString(undefined, { maximumFractionDigits: digits })
);

const formatPercent = (value: number | null | undefined, digits = 1): string => (
  value === null || value === undefined ? '—' : `${formatNumber(value, digits)}%`
);

const formatCurrency = (value: number | null | undefined, digits = 2): string => (
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value)
);

const formatSignedPoints = (value: number | null | undefined): string => (
  value === null || value === undefined ? '—' : `${value >= 0 ? '+' : ''}${formatNumber(value, 2)} pp`
);

const formatUpdatedAt = (value: string): string => {
  const generatedAt = new Date(value);
  if (Number.isNaN(generatedAt.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(generatedAt);
};

const formatAsOfDate = (value: string): string => {
  const asOf = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(asOf.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(asOf);
};

const apiErrorMessage = (cause: unknown, fallback: string): string => {
  if (typeof cause !== 'object' || cause === null) return fallback;
  const detail = (cause as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : fallback;
};

const refreshErrorMessage = (
  rawMessage: string | null | undefined,
  mode: 'readiness' | 'explore',
): string => {
  const message = rawMessage?.trim() ?? '';
  if (
    message.toLocaleLowerCase().includes('no historical production runs')
    || message.toLocaleLowerCase().includes('no rows')
    || message.toLocaleLowerCase().includes('no data')
  ) {
    return mode === 'readiness'
      ? 'We could not refresh this SKU Readiness report because no matching production runs were found.'
      : 'We could not refresh this Flexible Analysis report because no matching RedZone records were found.';
  }
  return mode === 'readiness'
    ? 'We could not refresh the RedZone SKU Readiness report. Please try again.'
    : 'We could not refresh the RedZone Flexible Analysis report. Please try again.';
};

const cautionClass = (level: string): string => `sku-caution-badge sku-caution-${level.toLowerCase()}`;

const formatSkuLabel = (sku: string, productTypeName?: string | null): string => {
  const name = productTypeName?.trim();
  return name ? `${sku} (${name})` : sku;
};

const KpiCard: React.FC<{
  label: string;
  value: string;
  detail: string;
  tone: 'teal' | 'red' | 'amber' | 'blue' | 'navy';
  icon: React.ReactNode;
}> = ({ label, value, detail, tone, icon }) => (
  <article className={`sku-kpi sku-kpi-${tone}`}>
    <div className="sku-kpi-label"><span>{label}</span>{icon}</div>
    <div className="sku-kpi-value">{value}</div>
    <div className="sku-kpi-detail">{detail}</div>
  </article>
);

const SectionCard: React.FC<{
  eyebrow: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ eyebrow, title, subtitle, icon, children, className = '' }) => (
  <section className={`sku-panel ${className}`}>
    <div className="sku-panel-head">
      <div>
        <div className="sku-eyebrow">{icon}{eyebrow}</div>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

const buildAreaChart = (areas: RedzoneDowntimeArea[]) => ({
  animationDuration: 350,
  color: ['#6e3450', '#67adca'],
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    valueFormatter: (value: number) => `${formatNumber(value, 1)}%`,
  },
  legend: { bottom: 0, textStyle: { color: '#7b6b72', fontSize: 11 } },
  grid: { left: 116, right: 24, top: 20, bottom: 44 },
  xAxis: {
    type: 'value',
    min: 0,
    max: 100,
    axisLabel: { color: '#a8989f', fontSize: 10, formatter: '{value}%' },
    splitLine: { lineStyle: { color: '#ede6e1' } },
  },
  yAxis: {
    type: 'category',
    inverse: true,
    data: areas.map((row) => row.area_name),
    axisLabel: { color: '#3f3338', fontSize: 11, width: 100, overflow: 'truncate' },
    axisLine: { show: false },
    axisTick: { show: false },
  },
  series: [
    { name: 'Actual OEE', type: 'bar', barMaxWidth: 12, data: areas.map((row) => numberValue(row.average_oee_pct)) },
    { name: 'Target OEE', type: 'bar', barMaxWidth: 12, data: areas.map((row) => numberValue(row.average_target_oee_pct)) },
  ],
});

const buildProblemHoursChart = (problems: RedzoneDowntimeProblem[]) => {
  const rows = [...problems]
    .sort((left, right) => numberValue(right.total_hours_lost) - numberValue(left.total_hours_lost))
    .slice(0, 8)
    .reverse();
  return {
    animationDuration: 350,
    color: '#b23a48',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (value: number) => `${formatNumber(value, 2)} hours`,
    },
    grid: { left: 130, right: 24, top: 16, bottom: 24 },
    xAxis: {
      type: 'value',
      axisLabel: { color: '#a8989f', fontSize: 10, formatter: '{value}h' },
      splitLine: { lineStyle: { color: '#ede6e1' } },
    },
    yAxis: {
      type: 'category',
      data: rows.map((row) => row.problem_type_asset_name || row.problem_type_name || row.location_name),
      axisLabel: { color: '#3f3338', fontSize: 10, width: 116, overflow: 'truncate' },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [{ type: 'bar', barMaxWidth: 18, data: rows.map((row) => numberValue(row.total_hours_lost)) }],
  };
};

const impactSort = (left: RedzoneDowntimeProblem, right: RedzoneDowntimeProblem): number => (
  numberValue(right.total_hours_lost) - numberValue(left.total_hours_lost)
  || numberValue(right.affected_runs) - numberValue(left.affected_runs)
  || numberValue(right.problem_event_count) - numberValue(left.problem_event_count)
);

const hierarchyValue = (row: RedzoneDowntimeProblem, level: RedzoneDowntimeHierarchyLevel): string => {
  const value = (row as unknown as Record<string, unknown>)[level.field];
  return typeof value === 'string' && value.trim() ? value : 'Unspecified';
};

type DrilldownTreeNode = {
  key: string;
  level: RedzoneDowntimeHierarchyLevel;
  row: RedzoneDowntimeProblem;
  children: DrilldownTreeNode[];
};

const hierarchyPathKey = (
  row: RedzoneDowntimeProblem,
  levels: RedzoneDowntimeHierarchyLevel[],
  fromIndex: number,
  toIndex: number,
): string => levels
  .slice(fromIndex, toIndex + 1)
  .map((level) => `${level.key}:${hierarchyValue(row, level)}`)
  .join('¦');

const buildDrilldownTree = (
  rows: RedzoneDowntimeProblem[],
  levels: RedzoneDowntimeHierarchyLevel[],
  selectedIndex: number,
): DrilldownTreeNode[] => {
  const nodeByPath = new Map<string, DrilldownTreeNode>();
  const roots: DrilldownTreeNode[] = [];
  const hierarchyRows = rows
    .map((row) => ({ row, levelIndex: levels.findIndex((level) => level.key === row.hierarchy_level) }))
    .filter(({ levelIndex }) => levelIndex >= selectedIndex)
    .sort((left, right) => left.levelIndex - right.levelIndex);

  hierarchyRows.forEach(({ row, levelIndex }) => {
    let parent: DrilldownTreeNode | undefined;
    for (let currentIndex = selectedIndex; currentIndex <= levelIndex; currentIndex += 1) {
      const level = levels[currentIndex];
      const key = hierarchyPathKey(row, levels, selectedIndex, currentIndex);
      let node = nodeByPath.get(key);
      if (!node) {
        node = {
          key,
          level,
          row,
          children: [],
        };
        nodeByPath.set(key, node);
        if (parent) parent.children.push(node);
        else roots.push(node);
      }
      parent = node;
    }
  });

  const sortAndLimit = (nodes: DrilldownTreeNode[]): DrilldownTreeNode[] => nodes
    .sort((left, right) => impactSort(left.row, right.row))
    .slice(0, 3)
    .map((node) => ({ ...node, children: sortAndLimit(node.children) }));

  return sortAndLimit(roots);
};

const DrilldownExplorer: React.FC<{
  levels: RedzoneDowntimeHierarchyLevel[];
  rows: RedzoneDowntimeProblem[];
}> = ({ levels, rows }) => {
  const [selectedLevel, setSelectedLevel] = useState(levels[0]?.key ?? 'AREA');
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());
  const selectedIndex = Math.max(0, levels.findIndex((level) => level.key === selectedLevel));
  const selectedLevelDefinition = levels[selectedIndex];
  const tree = buildDrilldownTree(rows, levels, selectedIndex);

  const toggleNode = (key: string): void => {
    setCollapsedNodes((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderTreeRows = (nodes: DrilldownTreeNode[], depth = 0): React.ReactNode[] => nodes.flatMap((node) => {
    const hasChildren = node.children.length > 0;
    const isCollapsed = collapsedNodes.has(node.key);
    const childRows = hasChildren && !isCollapsed ? renderTreeRows(node.children, depth + 1) : [];
    return [
      <tr key={node.key}>
        <td>
          <div className="sku-tree-node" style={{ paddingLeft: `${depth * 1.35}rem` }}>
            {hasChildren ? (
              <button
                type="button"
                className="sku-tree-toggle"
                onClick={() => toggleNode(node.key)}
                aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${hierarchyValue(node.row, node.level)}`}
                aria-expanded={!isCollapsed}
              >
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
            ) : <span className="sku-tree-toggle-spacer" />}
            <div>
              <strong>{hierarchyValue(node.row, node.level)}</strong>
              <small>{node.level.label}</small>
            </div>
          </div>
        </td>
        <td><span className={cautionClass(node.row.caution_level)}>{node.row.caution_level}</span></td>
        <td className="sku-number">{formatNumber(node.row.affected_runs, 0)} / {formatNumber(node.row.historical_runs, 0)}</td>
        <td className="sku-number">{formatPercent(node.row.frequency_pct, 2)}</td>
        <td className="sku-number">{formatNumber(node.row.problem_event_count, 0)}</td>
        <td className="sku-number">{formatNumber(node.row.total_hours_lost, 2)}</td>
        <td className="sku-number">{formatNumber(node.row.average_minutes_lost_per_event, 1)}</td>
      </tr>,
      ...childRows,
    ];
  });

  return (
    <div className="sku-drilldown-explorer">
      <div className="sku-drilldown-toolbar">
        <label className="sku-level-selector">
          <span>What to check first</span>
          <select value={selectedLevel} onChange={(event) => {
            setSelectedLevel(event.target.value);
            setCollapsedNodes(new Set());
          }}>
            {levels.map((level) => <option key={level.key} value={level.key}>{level.label}</option>)}
          </select>
        </label>
        <p>Explore the Availability hierarchy from {selectedLevelDefinition?.label || 'the selected level'}. Expand or collapse each parent to follow its affected children.</p>
      </div>

      {tree.length ? (
        <div className="sku-table-wrap sku-drilldown-tree-wrap">
          <table className="sku-table sku-drilldown-table">
            <thead><tr><th>Lineage</th><th>Caution</th><th className="sku-number">Affected runs</th><th className="sku-number">Frequency</th><th className="sku-number">Events</th><th className="sku-number">Hours lost</th><th className="sku-number">Avg min / event</th></tr></thead>
            <tbody>{renderTreeRows(tree)}</tbody>
          </table>
        </div>
      ) : <div className="sku-empty">No Availability evidence was found at or below the selected level.</div>}
    </div>
  );
};

const ImpactHighlights: React.FC<{ payload: RedzoneDowntimePayload }> = ({ payload }) => {
  const highlights = [
    { label: 'Most affecting location', row: payload.priority_path.top_locations[0], primary: (row: RedzoneDowntimeProblem) => row.location_name, secondary: (row: RedzoneDowntimeProblem) => row.area_name },
    { label: 'Top problem type there', row: payload.priority_path.top_problem_types[0], primary: (row: RedzoneDowntimeProblem) => row.problem_type_name, secondary: (row: RedzoneDowntimeProblem) => row.location_name },
    { label: 'Most affected asset type', row: payload.priority_path.top_asset_types[0], primary: (row: RedzoneDowntimeProblem) => row.problem_type_asset_type_name, secondary: (row: RedzoneDowntimeProblem) => row.problem_type_name },
    { label: 'Most affected asset', row: payload.priority_path.top_assets[0], primary: (row: RedzoneDowntimeProblem) => row.problem_type_asset_name, secondary: (row: RedzoneDowntimeProblem) => row.problem_type_asset_type_name },
  ];
  return (
    <section className="sku-impact-highlights">
      <div className="sku-impact-heading"><div className="sku-eyebrow"><AlertTriangle size={14} /> Impact lineage</div><p>Start with the location carrying the most Availability impact, then follow the evidence down the hierarchy.</p></div>
      {highlights.map((highlight) => <article className="sku-impact-card" key={highlight.label}>
        <span>{highlight.label}</span>
        {highlight.row ? <><strong>{highlight.primary(highlight.row)}</strong><small>{highlight.secondary(highlight.row)} · {formatNumber(highlight.row.total_hours_lost, 2)} h lost · {formatNumber(highlight.row.affected_runs, 0)} runs</small></> : <strong className="sku-impact-empty">No recorded evidence</strong>}
      </article>)}
    </section>
  );
};

const OverheadImpactPanel: React.FC<{
  payload: Pick<RedzoneDowntimePayload, 'impact'>;
}> = ({ payload }) => {
  const impact = payload.impact;
  const formulas = impact?.kpi_formulas ?? [];
  const normalizedStatus = impact?.valuation_status?.trim().toLocaleLowerCase() || '';
  const valuationUnavailable = !impact
    || /unavailable|not[ _-]?valued|unit[ _-]?mismatch|missing|suppressed/.test(normalizedStatus);
  const hasValuation = !valuationUnavailable
    && typeof impact.estimated_overhead_exposure_usd === 'number'
    && Number.isFinite(impact.estimated_overhead_exposure_usd);
  const statusLabel = impact?.valuation_status?.trim()
    ? impact.valuation_status.replace(/[_-]+/g, ' ')
    : 'Re-run required';
  const potentialUnits = impact?.potential_units_lost;
  const potentialUnitsUom = impact?.potential_units_uom?.trim();
  const potentialUnitsLabel = potentialUnits === null || potentialUnits === undefined
    ? impact?.potential_units_by_uom
      ?.map(({ uom, potential_units_lost: value }) => `${formatNumber(value, 2)} ${uom}`)
      .join(' · ') || '—'
    : `${formatNumber(potentialUnits, 2)}${potentialUnitsUom ? ` ${potentialUnitsUom}` : ''}`;
  const coverageDetail = impact
    ? `${formatNumber(impact.valued_problem_events, 0)} of ${formatNumber(impact.total_problem_events, 0)} problem events valued`
    : 'This saved analysis predates labor-credit valuation';

  return (
    <section className="sku-overhead-panel" aria-labelledby="sku-overhead-title">
      <div className="sku-overhead-head">
        <div>
          <div className="sku-eyebrow"><DollarSign size={14} /> Quantified labor-credit impact</div>
          <h2 id="sku-overhead-title">Estimated labor-credit impact</h2>
          <p>Potential production not realized during merged Availability downtime, valued using the client-confirmed SKU labor credit per finished-goods unit. Non-labor overhead is excluded.</p>
        </div>
        <span className={`sku-valuation-status${hasValuation ? ' is-valued' : ''}`}>
          <ShieldCheck size={14} />
          {statusLabel}
        </span>
      </div>

      <div className="sku-overhead-layout">
        <article className="sku-overhead-primary">
          <span>Estimated labor-credit impact</span>
          <strong>{hasValuation ? formatCurrency(impact?.estimated_overhead_exposure_usd) : 'Not valued'}</strong>
          <p>{hasValuation
            ? 'Potential units multiplied by the applicable SKU labor credit; this is not a confirmed cash expense.'
            : 'A dollar estimate is withheld when the labor credit, production unit, or UOM alignment is unavailable.'}</p>
        </article>

        <div className="sku-overhead-metrics">
          <article>
            <span><Package size={14} /> Potential units</span>
            <strong title={potentialUnitsLabel}>{potentialUnitsLabel}</strong>
            <small>Run-selected standard rate × merged downtime</small>
          </article>
          <article>
            <span><Timer size={14} /> Merged downtime</span>
            <strong>{impact?.merged_downtime_hours === null || impact?.merged_downtime_hours === undefined
              ? '—'
              : `${formatNumber(impact.merged_downtime_hours, 2)} h`}</strong>
            <small>Overlapping problem intervals counted once</small>
          </article>
          <article>
            <span><DollarSign size={14} /> Labor credit / unit</span>
            <strong>{impact?.overhead_rate_per_uom === null || impact?.overhead_rate_per_uom === undefined
              ? '—'
              : `${formatCurrency(impact.overhead_rate_per_uom)}${potentialUnitsUom ? ` / ${potentialUnitsUom}` : ''}`}</strong>
            <small>SKU-level USD labor credit per displayed UOM</small>
          </article>
          <article>
            <span><ShieldCheck size={14} /> Valuation coverage</span>
            <strong>{formatPercent(impact?.valuation_coverage_pct, 1)}</strong>
            <small>{coverageDetail}</small>
          </article>
        </div>
      </div>

      {formulas.length ? (
        <div className="sku-kpi-formulas">
          <div className="sku-kpi-formulas-head">
            <div>
              <strong>How these KPIs are calculated</strong>
              <span>Definitions and exact source columns supplied by the backend analysis module.</span>
            </div>
            {impact?.assumption_note && <p>{impact.assumption_note}</p>}
          </div>
          <div className="sku-kpi-formula-grid">
            {formulas.map((formula) => (
              <article className="sku-kpi-formula" key={formula.key}>
                <div className="sku-kpi-formula-title">
                  <strong>{formula.label}</strong>
                  <code>{formula.value_field}</code>
                </div>
                <code className="sku-kpi-formula-expression">{formula.expression}</code>
                <p>{formula.explanation}</p>
                <div className="sku-kpi-formula-sources">
                  <span>Source columns</span>
                  <div>
                    {formula.source_columns.map((column) => (
                      <code key={column}>{column}</code>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      ) : (
        <div className="sku-kpi-formulas-empty">
          KPI calculation details are unavailable for this saved analysis. Re-run the analysis to load them from the backend.
        </div>
      )}
    </section>
  );
};

const AiInsightPanel: React.FC<{
  payload: Pick<RedzoneDowntimePayload, 'ai_insights'>;
}> = ({ payload }) => {
  const insights = payload.ai_insights;
  const generated = insights.status === 'generated';
  return (
    <SectionCard
      eyebrow="AI floor-manager guidance"
      title="What the evidence says to check first"
      subtitle="Claude interprets the measured Availability hierarchy and turns it into level-specific checks. The evidence and SQL remain the source of truth."
      icon={<Sparkles size={14} />}
      className="sku-ai-panel"
    >
      <div className="sku-ai-summary">
        <div className="sku-ai-priority">
          <span>Most affecting level</span>
          <strong>{insights.most_affecting_level.label}: {insights.most_affecting_level.value}</strong>
          <p>{insights.most_affecting_level.reason}</p>
        </div>
        <span className={`sku-ai-status${generated ? ' is-generated' : ''}`}>
          {generated ? `AI generated · ${insights.model || 'configured model'}` : 'Evidence-based fallback'}
        </span>
      </div>
      {insights.message && <div className="sku-ai-message"><Info size={14} />{insights.message}</div>}
      <div className="sku-ai-insights">
        {insights.level_insights.map((insight) => <article className="sku-ai-insight" key={insight.level_key}>
          <span>{insight.level_label}</span>
          <strong>{insight.headline}</strong>
          <p><b>Evidence:</b> {insight.evidence}</p>
          <p><b>Suggested check:</b> {insight.recommendation}</p>
        </article>)}
      </div>
      <div className="sku-ai-prescription">
        <div><span>Prescriptive sequence</span><strong>{insights.prescription.priority}</strong></div>
        <ol>{insights.prescription.actions.map((action, index) => <li key={`${action}-${index}`}>{action}</li>)}</ol>
        <small><b>Validate:</b> {insights.prescription.validation}</small>
      </div>
    </SectionCard>
  );
};

const buildFrequencyChart = (locations: RedzoneDowntimeLocation[]) => {
  const rows = [...locations]
    .filter((row) => numberValue(row.affected_runs) > 0)
    .sort((left, right) => numberValue(right.frequency_pct) - numberValue(left.frequency_pct))
    .slice(0, 8)
    .reverse();
  return {
    animationDuration: 350,
    color: '#6e3450',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (value: number) => `${formatNumber(value, 1)}% of runs`,
    },
    grid: { left: 126, right: 24, top: 16, bottom: 24 },
    xAxis: {
      type: 'value',
      axisLabel: { color: '#a8989f', fontSize: 10, formatter: '{value}%' },
      splitLine: { lineStyle: { color: '#ede6e1' } },
    },
    yAxis: {
      type: 'category',
      data: rows.map((row) => row.location_name),
      axisLabel: { color: '#3f3338', fontSize: 10, width: 112, overflow: 'truncate' },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [{ type: 'bar', barMaxWidth: 18, data: rows.map((row) => numberValue(row.frequency_pct)) }],
  };
};

const SkuSelector: React.FC<{
  options: RedzoneDowntimeSkuOption[];
  selected: string;
  searchQuery: string;
  onSearch: (query: string) => void;
  onChange: (sku: string) => void;
  label?: React.ReactNode;
  prominent?: boolean;
  disabled?: boolean;
}> = ({
  options,
  selected,
  searchQuery,
  onSearch,
  onChange,
  label = 'SKU / Product name',
  prominent = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = React.useRef<HTMLDivElement>(null);
  const query = searchQuery.trim().toLocaleLowerCase();
  const selectedOption = options.find((option) => option.sku === selected);
  const visibleOptions = query
    ? options.filter((option) => (
      formatSkuLabel(option.sku, option.product_type_name)
        .toLocaleLowerCase()
        .includes(query)
    ))
    : options;

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        onSearch('');
      }
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [isOpen, onSearch]);

  const chooseSku = (sku: string) => {
    onChange(sku);
    onSearch('');
    setIsOpen(false);
  };

  return (
    <div className={`sku-selector${prominent ? ' sku-selector-prominent' : ''}`} ref={selectorRef}>
      <span>{label}</span>
      <div className="sku-combobox">
        <input
          id="sku-combobox"
          className="sku-combobox-input"
          type="search"
          value={isOpen
            ? searchQuery
            : formatSkuLabel(selected, selectedOption?.product_type_name)}
          onFocus={() => {
            if (disabled) return;
            setIsOpen(true);
            onSearch('');
          }}
          onChange={(event) => {
            if (disabled) return;
            setIsOpen(true);
            onSearch(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setIsOpen(false);
              onSearch('');
            } else if (event.key === 'Enter' && visibleOptions.length === 1) {
              event.preventDefault();
              chooseSku(visibleOptions[0].sku);
            }
          }}
          placeholder="Search SKU or product name..."
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-label="Search and select SKU"
          aria-expanded={isOpen}
          aria-controls="sku-combobox-options"
        />
        <ChevronDown className={`sku-combobox-chevron${isOpen ? ' is-open' : ''}`} size={16} aria-hidden="true" />
        {isOpen && !disabled && (
          <div id="sku-combobox-options" className="sku-combobox-menu" role="listbox" aria-label="Available SKUs">
            {visibleOptions.length ? visibleOptions.map((option) => (
              <button
                key={option.sku}
                type="button"
                className={`sku-combobox-option${option.sku === selected ? ' is-selected' : ''}`}
                role="option"
                aria-selected={option.sku === selected}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => chooseSku(option.sku)}
              >
                <strong>{formatSkuLabel(option.sku, option.product_type_name)}</strong>
                <span>{formatNumber(option.historical_runs, 0)} runs · OEE {formatPercent(option.average_oee_pct)}</span>
              </button>
            )) : <div className="sku-combobox-empty">No matching SKUs</div>}
          </div>
        )}
      </div>
    </div>
  );
};

type RedzonePageMode = 'readiness' | 'explore';

const AnalysisLevelSwitch: React.FC<{
  value: RedzonePageMode;
  onChange: (level: RedzonePageMode) => void;
}> = ({ value, onChange }) => (
  <div className="scope-level-switch" aria-label="Analysis level">
    {([
      ['readiness', 'SKU readiness', Package],
      ['explore', 'Flexible analysis', Layers3],
    ] as const).map(([level, label, Icon]) => (
      <button
        key={level}
        type="button"
        className={value === level ? 'is-active' : ''}
        onClick={() => onChange(level)}
        aria-pressed={value === level}
      >
        <Icon size={14} />
        <span>{label}</span>
      </button>
    ))}
  </div>
);

const DYNAMIC_DIMENSIONS: Array<{ key: RedzoneDynamicDimension; label: string }> = [
  { key: 'sku', label: 'SKU' },
  { key: 'area', label: 'Area' },
  { key: 'location', label: 'Location' },
  { key: 'problem_type', label: 'Problem Type' },
  { key: 'asset_type', label: 'Asset Type' },
  { key: 'asset_name', label: 'Asset Name' },
];

type DynamicViewDimension = RedzoneDynamicDimension;
const VIEW_DIMENSIONS = DYNAMIC_DIMENSIONS as Array<{ key: DynamicViewDimension; label: string }>;

const dynamicFilterSignature = (filters: RedzoneDynamicFilters): string => (
  JSON.stringify(DYNAMIC_DIMENSIONS.map(({ key }) => [key, [...(filters[key] ?? [])].sort()]))
);

const aggregateDynamicFacets = (
  options: RedzoneDynamicFacet[],
): RedzoneDynamicFacet[] => Array.from(
  options.reduce((byValue, option) => {
    const existing = byValue.get(option.value);
    byValue.set(option.value, existing
      ? {
        ...existing,
        record_count: existing.record_count + option.record_count,
      }
      : option);
    return byValue;
  }, new Map<string, RedzoneDynamicFacet>()).values(),
);

type DynamicAnalysisSelection = {
  plant: string;
  grain: DynamicViewDimension;
  primaryValues: string[];
  filters: RedzoneDynamicFilters;
  startDate: string;
  endDate: string;
  plannedMode: RedzonePlannedMode;
};

type SearchableSelectOption = {
  value: string;
  label: string;
  meta?: string;
};

const DYNAMIC_ALL_VALUES_TOKEN = '__ALL__';

const todayLocalDateInputValue = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SearchableSelect: React.FC<{
  value: string;
  options: SearchableSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  ariaLabel: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({
  value,
  options,
  placeholder,
  searchPlaceholder,
  ariaLabel,
  disabled = false,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const listboxId = React.useId();
  const selectedOption = options.find((option) => option.value === value);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter((option) => (
      `${option.label} ${option.meta ?? ''}`.toLocaleLowerCase().includes(normalizedQuery)
    ))
    : options;

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [isOpen]);

  const chooseOption = (nextValue: string) => {
    onChange(nextValue);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div className={`dynamic-search-select${disabled ? ' is-disabled' : ''}`} ref={wrapperRef}>
      <div className="dynamic-search-select-control">
        <Search size={14} aria-hidden="true" />
        <input
          type="search"
          value={isOpen ? query : selectedOption?.label ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          aria-controls={listboxId}
          onFocus={() => {
            setIsOpen(true);
            setQuery('');
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setIsOpen(false);
              setQuery('');
            } else if (event.key === 'Enter' && visibleOptions.length === 1) {
              event.preventDefault();
              chooseOption(visibleOptions[0].value);
            }
          }}
        />
        <ChevronDown
          size={15}
          className={isOpen ? 'is-open' : ''}
          aria-hidden="true"
        />
      </div>
      {isOpen && !disabled && (
        <div
          id={listboxId}
          className="dynamic-search-select-menu"
          role="listbox"
          aria-label={searchPlaceholder}
        >
          <div className="dynamic-search-select-hint">{searchPlaceholder}</div>
          {visibleOptions.length ? visibleOptions.map((option) => (
            <button
              type="button"
              key={option.value}
              className={option.value === value ? 'is-selected' : ''}
              role="option"
              aria-selected={option.value === value}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => chooseOption(option.value)}
            >
              <span>{option.label}</span>
              {option.meta && <small>{option.meta}</small>}
            </button>
          )) : (
            <div className="dynamic-search-select-empty">No matching values</div>
          )}
        </div>
      )}
    </div>
  );
};

const SearchableMultiSelect: React.FC<{
  values: string[];
  options: SearchableSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  ariaLabel: string;
  disabled?: boolean;
  allMeansEmpty?: boolean;
  onChange: (values: string[]) => void;
}> = ({
  values,
  options,
  placeholder,
  searchPlaceholder,
  ariaLabel,
  disabled = false,
  allMeansEmpty = false,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const listboxId = React.useId();
  const selectedValues = new Set(values);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter((option) => (
      `${option.label} ${option.meta ?? ''}`.toLocaleLowerCase().includes(normalizedQuery)
    ))
    : options;
  const hasAllToken = values.includes(DYNAMIC_ALL_VALUES_TOKEN);
  const allSelected = hasAllToken || (
    options.length > 0 && options.every((option) => selectedValues.has(option.value))
  );
  const displayValue = hasAllToken || (allMeansEmpty && values.length === 0)
    ? placeholder
    : values.length
      ? `${formatNumber(values.length, 0)} selected`
      : placeholder;
  const usesPlaceholderDisplay = hasAllToken || values.length === 0;

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [isOpen]);

  const toggleValue = (value: string) => {
    if (allSelected) {
      onChange(options.map((option) => option.value).filter((item) => item !== value));
      return;
    }
    const next = selectedValues.has(value)
      ? values.filter((item) => item !== value)
      : [...values, value];
    onChange(next);
  };

  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
      return;
    }
    if (allMeansEmpty) {
      onChange([DYNAMIC_ALL_VALUES_TOKEN]);
      return;
    }
    onChange(options.map((option) => option.value));
  };

  return (
    <div
      className={`dynamic-search-select${disabled ? ' is-disabled' : ''}${usesPlaceholderDisplay ? ' is-placeholder-display' : ''}`}
      ref={wrapperRef}
    >
      <div className="dynamic-search-select-control">
        <Search size={14} aria-hidden="true" />
        <input
          type="search"
          value={isOpen ? query : displayValue}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          aria-controls={listboxId}
          onFocus={() => {
            setIsOpen(true);
            setQuery('');
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setIsOpen(false);
              setQuery('');
            }
          }}
        />
        <ChevronDown
          size={15}
          className={isOpen ? 'is-open' : ''}
          aria-hidden="true"
        />
      </div>
      {isOpen && !disabled && (
        <div
          id={listboxId}
          className="dynamic-search-select-menu dynamic-multi-select-menu"
          role="listbox"
          aria-label={searchPlaceholder}
        >
          <div className="dynamic-search-select-hint">{searchPlaceholder}</div>
          <button
            type="button"
            className={`dynamic-multi-option dynamic-multi-option-all${allSelected ? ' is-selected' : ''}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={toggleAll}
          >
            <span>All</span>
          </button>
          {visibleOptions.length ? visibleOptions.map((option) => (
            <button
              type="button"
              key={option.value}
              className={`dynamic-multi-option${allSelected || selectedValues.has(option.value) ? ' is-selected' : ''}`}
              role="option"
              aria-selected={allSelected || selectedValues.has(option.value)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => toggleValue(option.value)}
            >
              <input type="checkbox" checked={allSelected || selectedValues.has(option.value)} readOnly tabIndex={-1} />
              <span>{option.label}</span>
              {option.meta && <small>{option.meta}</small>}
            </button>
          )) : (
            <div className="dynamic-search-select-empty">No matching values</div>
          )}
        </div>
      )}
    </div>
  );
};

const dynamicRequestFilters = (
  selection: DynamicAnalysisSelection,
): RedzoneDynamicFilters => {
  const next = { ...selection.filters };
  if (
    selection.primaryValues.length
    && !selection.primaryValues.includes(DYNAMIC_ALL_VALUES_TOKEN)
  ) {
    next[selection.grain] = selection.primaryValues;
  }
  return next;
};

const exclusiveEndMatches = (exclusiveEnd: string | null | undefined, selectedEndDate: string): boolean => {
  if (!exclusiveEnd || !selectedEndDate) return false;
  const expected = new Date(`${selectedEndDate}T00:00:00Z`).getTime() + 24 * 60 * 60 * 1000;
  const actual = new Date(exclusiveEnd).getTime();
  return Number.isFinite(actual) && actual === expected;
};

const savedDateRangeMatches = (
  range: { start_datetime: string | null; end_datetime: string | null } | null | undefined,
  startDate: string,
  endDate: string,
): boolean => (
  Boolean(range)
  && (range?.start_datetime ?? '').startsWith(startDate)
  && exclusiveEndMatches(range?.end_datetime, endDate)
);

const plannedModeValues = (mode: RedzonePlannedMode): string[] => {
  if (mode === 'both') return [DYNAMIC_ALL_VALUES_TOKEN];
  return [mode];
};

const plannedModeFromValues = (values: string[]): RedzonePlannedMode => {
  if (values.length === 0 || values.includes(DYNAMIC_ALL_VALUES_TOKEN)) return 'both';
  const selected = new Set(values);
  if (selected.has('unplanned') && selected.has('planned')) return 'both';
  if (selected.has('planned')) return 'planned';
  return 'unplanned';
};

const AnalysisActionButtons: React.FC<{
  canRun: boolean;
  loading: boolean;
  current?: boolean;
  onViewSaved: () => void;
  onRefreshReport: () => void;
}> = ({
  canRun,
  loading,
  current = false,
  onViewSaved,
  onRefreshReport,
}) => (
  <div className="dynamic-analysis-action-bar">
    <button
      type="button"
      className="dynamic-generate"
      onClick={onViewSaved}
      disabled={!canRun || loading || current}
    >
      <BarChart3 size={14} />
      {current ? 'Analysis current' : 'View saved analysis'}
    </button>
    <button
      type="button"
      className="dynamic-generate"
      onClick={onRefreshReport}
      disabled={!canRun || loading}
    >
      <RefreshCw size={14} className={loading ? 'is-spinning' : ''} />
      {loading ? 'Refreshing analysis...' : 'Refresh analysis'}
    </button>
  </div>
);

const DynamicAnalysisBuilder: React.FC<{
  plant: string;
  grain: DynamicViewDimension | '';
  primaryValues: string[];
  onPlantChange: (plant: string) => void;
  onGrainChange: (grain: DynamicViewDimension | '') => void;
  onPrimaryValuesChange: (values: string[]) => void;
  facets: RedzoneDynamicFacet[];
  filters: RedzoneDynamicFilters;
  onFiltersChange: (filters: RedzoneDynamicFilters) => void;
  startDate: string;
  endDate: string;
  plannedMode: RedzonePlannedMode;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  onPlannedModeChange: (mode: RedzonePlannedMode) => void;
  hasReport: boolean;
  isDirty: boolean;
  onViewSaved: () => void;
  onRefreshReport: () => void;
  loading: boolean;
}> = ({
  plant,
  grain,
  primaryValues,
  onPlantChange,
  onGrainChange,
  onPrimaryValuesChange,
  facets,
  filters,
  onFiltersChange,
  startDate,
  endDate,
  plannedMode,
  onDateRangeChange,
  onPlannedModeChange,
  hasReport,
  isDirty,
  onViewSaved,
  onRefreshReport,
  loading,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [openFilter, setOpenFilter] = useState<RedzoneDynamicDimension | null>(null);
  const [openFilterBounds, setOpenFilterBounds] = useState<DOMRect | null>(null);
  const [filterQueries, setFilterQueries] = useState<
    Partial<Record<RedzoneDynamicDimension, string>>
  >({});
  useEffect(() => {
    if (!openFilter) return undefined;
    const close = () => {
      setOpenFilter(null);
      setOpenFilterBounds(null);
    };
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('resize', close);
    };
  }, [openFilter]);
  const toggleFilter = (
    dimension: RedzoneDynamicDimension,
    value: string,
    availableValues: string[] = [],
  ) => {
    const selected = filters[dimension] ?? [];
    const nextRaw = selected.length === 0 && availableValues.length
      ? availableValues.filter((item) => item !== value)
      : selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value];
    const uniqueAvailableValues = Array.from(new Set(availableValues));
    const next = uniqueAvailableValues.length > 0
      && nextRaw.length === uniqueAvailableValues.length
      && uniqueAvailableValues.every((item) => nextRaw.includes(item))
      ? []
      : nextRaw;
    const updated: RedzoneDynamicFilters = { ...filters, [dimension]: next };
    if (dimension === 'area' && next.length > 0) {
      const allowedLocations = new Set(
        facets
          .filter((facet) => facet.dimension === 'location' && next.includes(facet.parent_value ?? ''))
          .map((facet) => facet.value),
      );
      updated.location = (filters.location ?? []).filter((item) => allowedLocations.has(item));
    }
    if (dimension === 'problem_type' && next.length > 0) {
      const allowedAssetTypes = new Set(
        facets
          .filter((facet) => (
            facet.dimension === 'asset_type'
            && next.includes(facet.parent_value ?? '')
          ))
          .map((facet) => facet.value),
      );
      updated.asset_type = (filters.asset_type ?? []).filter(
        (item) => allowedAssetTypes.has(item),
      );
      const retainedAssetTypes = updated.asset_type ?? [];
      if (retainedAssetTypes.length > 0) {
        const allowedAssets = new Set(
          facets
            .filter((facet) => (
              facet.dimension === 'asset_name'
              && retainedAssetTypes.includes(facet.parent_value ?? '')
            ))
            .map((facet) => facet.value),
        );
        updated.asset_name = (filters.asset_name ?? []).filter(
          (item) => allowedAssets.has(item),
        );
      }
    }
    if (dimension === 'asset_type' && next.length > 0) {
      const allowedAssets = new Set(
        facets
          .filter((facet) => facet.dimension === 'asset_name' && next.includes(facet.parent_value ?? ''))
          .map((facet) => facet.value),
      );
      updated.asset_name = (filters.asset_name ?? []).filter((item) => allowedAssets.has(item));
    }
    onFiltersChange(updated);
  };
  const optionalDimensions = VIEW_DIMENSIONS.filter(({ key }) => key !== grain);
  const activeEntries = optionalDimensions.flatMap(({ key, label }) => (
    (filters[key] ?? []).map((value) => ({ dimension: key, label, value }))
  ));
  const plantOptions = aggregateDynamicFacets(facets.filter((facet) => facet.dimension === 'plant'));
  const primaryOptions = grain
    ? aggregateDynamicFacets(facets.filter((facet) => facet.dimension === grain))
    : [];
  const specificPrimaryValues = primaryValues.filter((value) => value !== DYNAMIC_ALL_VALUES_TOKEN);
  const primaryLabel = VIEW_DIMENSIONS.find(({ key }) => key === grain)?.label;
  const activeFilterCount = activeEntries.length;
  const maxSelectableDate = todayLocalDateInputValue();
  const maxStartDate = endDate && endDate < maxSelectableDate ? endDate : maxSelectableDate;
  const plannedOptions: SearchableSelectOption[] = [
    { value: 'unplanned', label: 'Unplanned' },
    { value: 'planned', label: 'Planned' },
  ];

  return (
    <section
      className={`dynamic-builder ${hasReport ? 'dynamic-builder-sticky' : ''}`}
      aria-label="Flexible analysis controls"
    >
      <div className="dynamic-builder-primary dynamic-builder-top-row dynamic-builder-control-grid">
        <div className="dynamic-grain-selector">
          <span>Plant <b>*</b></span>
          <SearchableSelect
            value={plant}
            options={plantOptions.map((option) => ({
              value: option.value,
              label: option.label,
              meta: `${formatNumber(option.record_count, 0)} records`,
            }))}
            placeholder="Select plant"
            searchPlaceholder="Search plants"
            ariaLabel="Search and select plant"
            onChange={onPlantChange}
            disabled={loading}
          />
        </div>
        <div className="dynamic-grain-selector">
          <span>View by <b>*</b></span>
          <SearchableSelect
            value={grain}
            options={VIEW_DIMENSIONS.map((dimension) => ({
              value: dimension.key,
              label: dimension.label,
            }))}
            placeholder="Select analysis grain"
            searchPlaceholder="Search analysis grains"
            ariaLabel="Search and select analysis grain"
            onChange={(value) => onGrainChange(value as DynamicViewDimension)}
            disabled={loading || !plant}
          />
        </div>
        <div className="dynamic-grain-selector dynamic-primary-value">
          <span>View value <b>optional</b></span>
          <SearchableMultiSelect
            values={primaryValues}
            options={primaryOptions.map((option) => ({
              value: option.value,
              label: option.label,
              meta: `${formatNumber(option.record_count, 0)} records`,
            }))}
            placeholder={grain ? `All ${primaryLabel}` : 'Select a view first'}
            searchPlaceholder={grain ? `Search ${primaryLabel} values` : 'Select a view first'}
            ariaLabel={grain
              ? `Search and select ${primaryLabel}`
              : 'Select an analysis grain first'}
            allMeansEmpty
            onChange={onPrimaryValuesChange}
            disabled={loading || !plant || !grain}
          />
        </div>
        <fieldset className="dynamic-period-selector dynamic-period-inline">
          <legend><CalendarDays size={13} /> Date range <b>*</b></legend>
          <label>
            <span>Start date</span>
            <input
              type="date"
              value={startDate}
              max={maxStartDate}
              onChange={(event) => onDateRangeChange(event.target.value, endDate)}
              disabled={loading || !plant}
              aria-label="Analysis start date"
            />
          </label>
          <label>
            <span>End date</span>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              max={maxSelectableDate}
              onChange={(event) => onDateRangeChange(startDate, event.target.value)}
              disabled={loading || !plant}
              aria-label="Analysis end date"
            />
          </label>
        </fieldset>
        <fieldset className="dynamic-period-selector dynamic-planned-inline">
          <legend>Type <b>*</b></legend>
          <SearchableMultiSelect
            values={plannedModeValues(plannedMode)}
            options={plannedOptions}
            placeholder="All"
            searchPlaceholder="Search types"
            ariaLabel="Search and select type"
            disabled={loading || !plant}
            onChange={(values) => onPlannedModeChange(plannedModeFromValues(values))}
          />
        </fieldset>
      </div>
      {!expanded && (
        <div className="dynamic-builder-optional dynamic-builder-optional-collapsed">
          <div className="dynamic-filter-copy">
            <Filter size={15} />
            <div>
              <strong>Optional filters{activeFilterCount ? ` (${activeFilterCount})` : ''}</strong>
              <span>{plant ? 'Expand to constrain area, line, SKU, problem, or asset.' : 'Select a plant to enable filters.'}</span>
            </div>
            <button
              type="button"
              className="dynamic-options-toggle"
              onClick={() => setExpanded(true)}
              disabled={!plant}
              aria-expanded={expanded}
            >
              Expand
              <ChevronDown size={14} />
            </button>
          </div>
        </div>
      )}
      {expanded && (
        <div className={`dynamic-builder-optional${!plant ? ' is-disabled' : ''}`}>
          <div className="dynamic-filter-copy">
            <Filter size={15} />
            <div>
              <strong>Optional filters</strong>
              <span>{plant ? 'Constrain other grains or leave them as All.' : 'Select a plant to enable filters.'}</span>
            </div>
            <button
              type="button"
              className="dynamic-options-toggle"
              onClick={() => setExpanded(false)}
              disabled={!plant}
              aria-expanded={expanded}
            >
              Minimize
              <ChevronDown size={14} />
            </button>
          </div>
          <div className="dynamic-filter-grid">
        {optionalDimensions.map((dimension) => {
          const selected = filters[dimension.key] ?? [];
          const matchingOptions = facets.filter((facet) => {
            if (facet.dimension !== dimension.key) return false;
            if (dimension.key === 'area' && grain === 'location' && specificPrimaryValues.length > 0) {
              const parentAreas = new Set(
                facets
                  .filter((item) => (
                    item.dimension === 'location' && specificPrimaryValues.includes(item.value)
                  ))
                  .map((item) => item.parent_value),
              );
              return parentAreas.has(facet.value);
            }
            const selectedAreas = grain === 'area' && specificPrimaryValues.length > 0
              ? specificPrimaryValues
              : filters.area ?? [];
            if (dimension.key === 'location' && selectedAreas.length > 0) {
              return selectedAreas.includes(facet.parent_value ?? '');
            }
            const selectedProblemTypes = grain === 'problem_type' && specificPrimaryValues.length > 0
              ? specificPrimaryValues
              : filters.problem_type ?? [];
            if (dimension.key === 'asset_type' && selectedProblemTypes.length > 0) {
              return selectedProblemTypes.includes(facet.parent_value ?? '');
            }
            const selectedAssetTypes = grain === 'asset_type' && specificPrimaryValues.length > 0
              ? specificPrimaryValues
              : grain === 'asset_name' && specificPrimaryValues.length > 0
                ? facets
                  .filter((item) => (
                    item.dimension === 'asset_name' && specificPrimaryValues.includes(item.value)
                  ))
                  .map((item) => item.parent_value ?? '')
                  .filter(Boolean)
                : filters.asset_type ?? [];
            if (dimension.key === 'problem_type' && selectedAssetTypes.length > 0) {
              const parentProblemTypes = new Set(
                facets
                  .filter((item) => (
                    item.dimension === 'asset_type'
                    && selectedAssetTypes.includes(item.value)
                  ))
                  .map((item) => item.parent_value),
              );
              return parentProblemTypes.has(facet.value);
            }
            if (dimension.key === 'asset_name' && selectedAssetTypes.length > 0) {
              return selectedAssetTypes.includes(facet.parent_value ?? '');
            }
            if (dimension.key === 'asset_type' && grain === 'asset_name' && specificPrimaryValues.length > 0) {
              const parentAssetTypes = new Set(
                facets
                  .filter((item) => (
                    item.dimension === 'asset_name' && specificPrimaryValues.includes(item.value)
                  ))
                  .map((item) => item.parent_value),
              );
              return parentAssetTypes.has(facet.value);
            }
            return true;
          });
          const options = aggregateDynamicFacets(matchingOptions);
          const availableValues = options.map((option) => option.value);
          const query = filterQueries[dimension.key] ?? '';
          const normalizedQuery = query.trim().toLocaleLowerCase();
          const visibleOptions = normalizedQuery
            ? options.filter((option) => (
              `${option.label} ${option.parent_value ?? ''}`
                .toLocaleLowerCase()
                .includes(normalizedQuery)
            ))
            : options;
          return (
            <div className="dynamic-filter-field" key={dimension.key}>
              <span className="dynamic-filter-label">{dimension.label}</span>
              <div className={`dynamic-filter${openFilter === dimension.key ? ' is-open' : ''}`}>
              <button
                type="button"
                className="dynamic-filter-trigger"
                disabled={!plant}
                onClick={(event) => {
                  if (openFilter === dimension.key) {
                    setOpenFilter(null);
                    setOpenFilterBounds(null);
                    return;
                  }
                  setOpenFilter(dimension.key);
                  setOpenFilterBounds(event.currentTarget.getBoundingClientRect());
                }}
              >
                <strong>{selected.length ? `${selected.length} selected` : 'All'}</strong>
                <ChevronDown size={14} />
              </button>
              {openFilter === dimension.key && openFilterBounds && createPortal(
              <div
                className="dynamic-filter-menu"
                style={{
                  position: 'fixed',
                  top: openFilterBounds.bottom + 6,
                  left: openFilterBounds.left,
                  width: Math.max(openFilterBounds.width, 272),
                }}
              >
                <div className="dynamic-filter-menu-head">
                  <span>{options.length} values</span>
                  {selected.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onFiltersChange({ ...filters, [dimension.key]: [] })}
                      disabled={!plant}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <label className="dynamic-filter-search">
                  <Search size={13} aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    placeholder={`Search ${dimension.label}`}
                    aria-label={`Search ${dimension.label} filter values`}
                    disabled={!plant}
                    onChange={(event) => setFilterQueries((current) => ({
                      ...current,
                      [dimension.key]: event.target.value,
                    }))}
                  />
                  {query && (
                    <button
                      type="button"
                      aria-label={`Clear ${dimension.label} search`}
                      disabled={!plant}
                      onClick={() => setFilterQueries((current) => ({
                        ...current,
                        [dimension.key]: '',
                      }))}
                    >
                      <X size={12} />
                    </button>
                  )}
                </label>
                <div className="dynamic-filter-options">
                  <label key={`${dimension.key}-all`} className="dynamic-filter-all-option">
                    <input
                      type="checkbox"
                      checked={selected.length === 0}
                      disabled={!plant}
                      onChange={() => onFiltersChange({ ...filters, [dimension.key]: [] })}
                    />
                    <span>All</span>
                  </label>
                  {visibleOptions.map((option) => (
                    <label key={`${option.parent_value || ''}-${option.value}`}>
                      <input
                        type="checkbox"
                        checked={selected.length === 0 || selected.includes(option.value)}
                        disabled={!plant}
                        onChange={() => toggleFilter(dimension.key, option.value, availableValues)}
                      />
                      <span>{option.label}</span>
                      <small>{formatNumber(option.record_count, 0)}</small>
                    </label>
                  ))}
                  {!visibleOptions.length && (
                    <div className="dynamic-filter-empty">No matching values</div>
                  )}
                </div>
              </div>,
              document.body,
              )}
            </div>
            </div>
          );
        })}
          </div>
          {activeEntries.length > 0 && (
            <div className="dynamic-filter-chips">
          <span>Active filters</span>
          {activeEntries.map((entry) => (
            <button
              type="button"
              key={`${entry.dimension}-${entry.value}`}
              onClick={() => toggleFilter(entry.dimension, entry.value)}
              title={`Remove ${entry.label} filter`}
            >
              <b>{entry.label}</b>{entry.value}<X size={12} />
            </button>
          ))}
          <button type="button" className="dynamic-clear-all" onClick={() => onFiltersChange({})}>
            Clear all
          </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const SkuReadinessControls: React.FC<{
  plant: string;
  sku: string;
  skuOptions: RedzoneDowntimeSkuOption[];
  plantOptions: RedzoneDynamicFacet[];
  skuSearch: string;
  startDate: string;
  endDate: string;
  loading: boolean;
  hasReport: boolean;
  onPlantChange: (plant: string) => void;
  onSkuChange: (sku: string) => void;
  onSkuSearch: (query: string) => void;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  onViewSaved: () => void;
  onRefreshReport: () => void;
}> = ({
  plant,
  sku,
  skuOptions,
  plantOptions,
  skuSearch,
  startDate,
  endDate,
  loading,
  hasReport,
  onPlantChange,
  onSkuChange,
  onSkuSearch,
  onDateRangeChange,
  onViewSaved,
  onRefreshReport,
}) => {
  const maxSelectableDate = todayLocalDateInputValue();
  const maxStartDate = endDate && endDate < maxSelectableDate ? endDate : maxSelectableDate;
  return (
    <section className={`dynamic-builder ${hasReport ? 'dynamic-builder-sticky' : ''}`} aria-label="SKU Readiness controls">
      <div className="dynamic-builder-primary dynamic-builder-top-row sku-readiness-control-grid">
        <div className="dynamic-grain-selector">
          <span>Plant <b>*</b></span>
          <SearchableSelect
            value={plant}
            options={plantOptions.map((option) => ({
              value: option.value,
              label: option.label,
              meta: `${formatNumber(option.record_count, 0)} records`,
            }))}
            placeholder="Select plant"
            searchPlaceholder="Search plants"
            ariaLabel="Search and select plant"
            onChange={onPlantChange}
            disabled={loading}
          />
        </div>
        <SkuSelector
          options={skuOptions}
          selected={sku}
          searchQuery={skuSearch}
          onSearch={onSkuSearch}
          onChange={onSkuChange}
          label={<>SKU / Product name <b>*</b></>}
          disabled={loading || !plant}
        />
        <fieldset className="dynamic-period-selector dynamic-period-inline">
          <legend><CalendarDays size={13} /> Date range <b>*</b></legend>
          <label>
            <span>Start date</span>
            <input
              type="date"
              value={startDate}
              max={maxStartDate}
              onChange={(event) => onDateRangeChange(event.target.value, endDate)}
              disabled={loading || !plant}
              aria-label="SKU Readiness start date"
            />
          </label>
          <label>
            <span>End date</span>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              max={maxSelectableDate}
              onChange={(event) => onDateRangeChange(startDate, event.target.value)}
              disabled={loading || !plant}
              aria-label="SKU Readiness end date"
            />
          </label>
        </fieldset>
      </div>
    </section>
  );
};

const DynamicImpactLineage: React.FC<{ payload: RedzoneDynamicPayload }> = ({ payload }) => {
  const dimensions: Array<{ key: RedzoneDynamicDimension; label: string }> = [
    { key: 'area', label: 'Most affecting area' },
    { key: 'location', label: 'Most affecting location' },
    { key: 'sku', label: 'Most affected SKU' },
    { key: 'problem_type', label: 'Most affected problem type' },
    { key: 'asset_type', label: 'Most affected asset type' },
    { key: 'asset_name', label: 'Most affected asset' },
  ];
  return (
    <section className="sku-impact-highlights dynamic-impact-lineage">
      <div className="sku-impact-heading">
        <div className="sku-eyebrow"><AlertTriangle size={14} /> Impact lineage</div>
        <p>Follow the filtered Availability evidence from production scope to the affected equipment.</p>
      </div>
      {dimensions.map(({ key, label }) => {
        const row = payload.impact_lineage[key];
        return (
          <article className="sku-impact-card" key={key}>
            <span>{label}</span>
            {row ? (
              <>
                <strong>{row.group_label}</strong>
                <small>{formatNumber(row.total_hours_lost, 2)} h lost · {formatNumber(row.affected_runs, 0)} runs</small>
              </>
            ) : <strong className="sku-impact-empty">No recorded evidence</strong>}
          </article>
        );
      })}
    </section>
  );
};

const buildDynamicParetoChart = (rows: RedzoneDynamicRow[]) => {
  const ranked = [...rows]
    .filter((row) => numberValue(row.total_hours_lost) > 0)
    .sort((left, right) => numberValue(right.total_hours_lost) - numberValue(left.total_hours_lost))
    .slice(0, 10);
  const totalHours = ranked.reduce((total, row) => total + numberValue(row.total_hours_lost), 0);
  let cumulativeHours = 0;
  const cumulativeShare = ranked.map((row) => {
    cumulativeHours += numberValue(row.total_hours_lost);
    return totalHours ? Number(((cumulativeHours / totalHours) * 100).toFixed(2)) : 0;
  });
  return {
    animationDuration: 350,
    color: ['#b23a48', '#6e3450'],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
    },
    legend: { bottom: 0, textStyle: { color: '#7b6b72', fontSize: 11 } },
    grid: { left: 58, right: 54, top: 28, bottom: 88 },
    xAxis: [{
      type: 'category',
      data: ranked.map((row) => row.group_label),
      axisLabel: {
        color: '#3f3338',
        fontSize: 10,
        interval: 0,
        rotate: 28,
        width: 90,
        overflow: 'truncate',
      },
      axisTick: { alignWithLabel: true },
    }],
    yAxis: [
      {
        type: 'value',
        name: 'Hours',
        axisLabel: { color: '#a8989f', fontSize: 10, formatter: '{value}h' },
        splitLine: { lineStyle: { color: '#ede6e1' } },
      },
      {
        type: 'value',
        name: 'Cumulative',
        min: 0,
        max: 100,
        axisLabel: { color: '#a8989f', fontSize: 10, formatter: '{value}%' },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'Hours lost',
        type: 'bar',
        barMaxWidth: 28,
        data: ranked.map((row) => numberValue(row.total_hours_lost)),
      },
      {
        name: 'Cumulative share',
        type: 'line',
        yAxisIndex: 1,
        smooth: false,
        symbolSize: 6,
        data: cumulativeShare,
      },
    ],
  };
};

const buildDynamicOeeChart = (rows: RedzoneDynamicRow[]) => {
  const ranked = [...rows]
    .filter((row) => row.average_oee_pct !== null)
    .sort((left, right) => (
      numberValue(left.average_oee_delta_pp) - numberValue(right.average_oee_delta_pp)
    ))
    .slice(0, 12);
  return {
    animationDuration: 350,
    color: ['#6e3450', '#67adca'],
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { bottom: 0, textStyle: { color: '#7b6b72', fontSize: 11 } },
    grid: { left: 142, right: 24, top: 16, bottom: 42 },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { color: '#a8989f', fontSize: 10, formatter: '{value}%' },
      splitLine: { lineStyle: { color: '#ede6e1' } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: ranked.map((row) => row.group_label),
      axisLabel: { color: '#3f3338', fontSize: 10, width: 128, overflow: 'truncate' },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      { name: 'Actual OEE', type: 'bar', barMaxWidth: 12, data: ranked.map((row) => numberValue(row.average_oee_pct)) },
      { name: 'Target OEE', type: 'bar', barMaxWidth: 12, data: ranked.map((row) => numberValue(row.average_target_oee_pct)) },
    ],
  };
};

const buildDynamicRecurrenceChart = (rows: RedzoneDynamicRow[]) => {
  const ranked = [...rows]
    .filter((row) => numberValue(row.problem_event_count) > 0)
    .sort((left, right) => numberValue(right.total_hours_lost) - numberValue(left.total_hours_lost))
    .slice(0, 20);
  const maxEvents = Math.max(...ranked.map((row) => numberValue(row.problem_event_count)), 1);
  return {
    animationDuration: 350,
    color: ['#6e3450'],
    tooltip: {
      trigger: 'item',
      formatter: (params: { data: { name: string; value: number[] } }) => (
        `<strong>${params.data.name}</strong><br/>`
        + `Affected-run frequency: ${formatNumber(params.data.value[0], 2)}%<br/>`
        + `Hours lost: ${formatNumber(params.data.value[1], 2)}<br/>`
        + `Events: ${formatNumber(params.data.value[2], 0)}`
      ),
    },
    grid: { left: 58, right: 30, top: 24, bottom: 48 },
    xAxis: {
      type: 'value',
      name: 'Affected-run frequency',
      axisLabel: { color: '#a8989f', fontSize: 10, formatter: '{value}%' },
      splitLine: { lineStyle: { color: '#ede6e1' } },
    },
    yAxis: {
      type: 'value',
      name: 'Hours lost',
      axisLabel: { color: '#a8989f', fontSize: 10, formatter: '{value}h' },
      splitLine: { lineStyle: { color: '#ede6e1' } },
    },
    series: [{
      type: 'scatter',
      data: ranked.map((row) => ({
        name: row.group_label,
        value: [
          numberValue(row.frequency_pct),
          numberValue(row.total_hours_lost),
          numberValue(row.problem_event_count),
        ],
        symbolSize: 10 + (numberValue(row.problem_event_count) / maxEvents) * 28,
      })),
      label: {
        show: ranked.length <= 10,
        position: 'top',
        color: '#3f3338',
        fontSize: 9,
        width: 90,
        overflow: 'truncate',
        formatter: '{b}',
      },
      emphasis: { focus: 'series' },
    }],
  };
};

const buildDynamicTreemapChart = (rows: RedzoneDynamicRow[]) => ({
  animationDuration: 350,
  tooltip: {
    formatter: (params: { name: string; value: number; data: { events: number; runs: number } }) => (
      `<strong>${params.name}</strong><br/>`
      + `Hours lost: ${formatNumber(params.value, 2)}<br/>`
      + `Affected runs: ${formatNumber(params.data.runs, 0)}<br/>`
      + `Events: ${formatNumber(params.data.events, 0)}`
    ),
  },
  series: [{
    type: 'treemap',
    roam: false,
    nodeClick: false,
    breadcrumb: { show: false },
    label: {
      show: true,
      color: '#fff',
      fontSize: 11,
      lineHeight: 15,
      formatter: '{b}',
    },
    itemStyle: { borderColor: '#fff', borderWidth: 2, gapWidth: 2 },
    levels: [{
      color: ['#51263b', '#6e3450', '#8b4564', '#a95d79', '#c17a91', '#d6a0af'],
      colorMappingBy: 'value',
    }],
    data: [...rows]
      .filter((row) => numberValue(row.total_hours_lost) > 0)
      .sort((left, right) => numberValue(right.total_hours_lost) - numberValue(left.total_hours_lost))
      .slice(0, 18)
      .map((row) => ({
        name: row.group_label,
        value: numberValue(row.total_hours_lost),
        events: numberValue(row.problem_event_count),
        runs: numberValue(row.affected_runs),
      })),
  }],
});

const DynamicSummaryBand: React.FC<{ payload: RedzoneDynamicPayload }> = ({ payload }) => {
  const { baseline } = payload;
  return (
    <section className="dynamic-summary-band" aria-label="Selected analysis summary">
      <article className="dynamic-summary-primary">
        <span><Gauge size={14} /> Production performance</span>
        <strong>{formatPercent(baseline.average_oee_pct, 2)}</strong>
        <p>
          Target {formatPercent(baseline.average_target_oee_pct, 2)}
          <b className={numberValue(baseline.average_oee_delta_pp) < 0 ? 'is-negative' : ''}>
            {formatSignedPoints(baseline.average_oee_delta_pp)}
          </b>
        </p>
      </article>
      <article>
        <span><Timer size={14} /> Availability impact</span>
        <strong>{formatNumber(baseline.availability_hours_lost, 2)} h</strong>
        <p>{formatNumber(baseline.availability_affected_runs, 0)} affected runs · {formatPercent(baseline.availability_frequency_pct, 2)}</p>
      </article>
      <article>
        <span><Route size={14} /> Production scope</span>
        <strong>{formatNumber(baseline.historical_runs, 0)} runs</strong>
        <p>{formatNumber(baseline.sku_count, 0)} SKUs · {formatNumber(baseline.location_count, 0)} locations</p>
      </article>
      <article>
        <span><AlertTriangle size={14} /> Evidence</span>
        <strong>{formatNumber(baseline.availability_problem_events, 0)}</strong>
        <p>Filtered Availability events across {formatNumber(baseline.area_count, 0)} areas</p>
      </article>
      <article>
        <span><Info size={14} /> Source rows</span>
        <strong>{formatNumber(payload.methodology.source_row_counts?.run_problem_rows ?? 0, 0)}</strong>
        <p>
          {formatNumber(payload.methodology.source_row_counts?.run_fact_rows ?? 0, 0)} runs · {formatNumber(payload.methodology.source_row_counts?.comment_rows ?? 0, 0)} comments
        </p>
      </article>
    </section>
  );
};

type DynamicHierarchyNode = {
  row: RedzoneDynamicRow;
  children: DynamicHierarchyNode[];
};

const DynamicHierarchy: React.FC<{ payload: RedzoneDynamicPayload }> = ({ payload }) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [startLevel, setStartLevel] = useState('area');
  useEffect(() => {
    setExpandedNodes(new Set());
  }, [payload.selection.configuration_key]);
  const nodeByKey = new Map<string, DynamicHierarchyNode>();
  payload.drilldowns.forEach((row) => nodeByKey.set(row.group_key, { row, children: [] }));
  const roots: DynamicHierarchyNode[] = [];
  [...nodeByKey.entries()].forEach(([key, node]) => {
    const separator = key.lastIndexOf('¦');
    const parent = separator >= 0 ? nodeByKey.get(key.slice(0, separator)) : undefined;
    if (parent) parent.children.push(node);
    if (node.row.grain === startLevel) roots.push(node);
  });
  const expandableKeys = [...nodeByKey.values()]
    .filter((node) => node.children.length > 0)
    .map((node) => node.row.group_key);
  const sortNodes = (nodes: DynamicHierarchyNode[]) => nodes
    .sort((left, right) => numberValue(right.row.total_hours_lost) - numberValue(left.row.total_hours_lost))
    .map((node) => ({ ...node, children: sortNodes(node.children) }));
  const renderNodes = (nodes: DynamicHierarchyNode[], depth = 0): React.ReactNode[] => sortNodes(nodes).flatMap((node) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedNodes.has(node.row.group_key);
    return [
      <tr key={node.row.group_key}>
        <td>
          <div className="sku-tree-node" style={{ paddingLeft: `${depth * 1.35}rem` }}>
            {hasChildren ? (
              <button
                type="button"
                className="sku-tree-toggle"
                onClick={() => setExpandedNodes((current) => {
                  const next = new Set(current);
                  if (next.has(node.row.group_key)) {
                    next.delete(node.row.group_key);
                  } else {
                    next.add(node.row.group_key);
                  }
                  return next;
                })}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.row.group_label}`}
              >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            ) : <span className="sku-tree-toggle-spacer" />}
            <div><strong>{node.row.group_label}</strong><small>{node.row.grain.replaceAll('_', ' ')}</small></div>
          </div>
        </td>
        <td><span className={cautionClass(node.row.caution_level)}>{node.row.caution_level}</span></td>
        <td className="sku-number">{formatNumber(node.row.affected_runs, 0)}</td>
        <td className="sku-number">{formatPercent(node.row.frequency_pct, 2)}</td>
        <td className="sku-number">{formatNumber(node.row.problem_event_count, 0)}</td>
        <td className="sku-number">{formatNumber(node.row.total_hours_lost, 2)}</td>
      </tr>,
      ...(hasChildren && isExpanded ? renderNodes(node.children, depth + 1) : []),
    ];
  });
  return (
    <>
      <div className="dynamic-hierarchy-controls">
        <div className="dynamic-hierarchy-level">
          <span>Start hierarchy from</span>
          <SearchableSelect
            value={startLevel}
            options={payload.hierarchy_levels.map((level) => ({
              value: level.key,
              label: level.label,
            }))}
            placeholder="Select a hierarchy level"
            searchPlaceholder="Search hierarchy levels"
            ariaLabel="Search and select hierarchy start level"
            onChange={(value) => {
              setStartLevel(value);
              setExpandedNodes(new Set());
            }}
          />
        </div>
        <div className="dynamic-hierarchy-actions" aria-label="Hierarchy expansion controls">
          <button
            type="button"
            onClick={() => setExpandedNodes(new Set(expandableKeys))}
            disabled={!expandableKeys.length || expandedNodes.size === expandableKeys.length}
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setExpandedNodes(new Set())}
            disabled={!expandedNodes.size}
          >
            Collapse all
          </button>
        </div>
        <small>Rows start collapsed. Expanding a row opens only its immediate children.</small>
      </div>
      {roots.length ? (
        <div className="sku-table-wrap sku-drilldown-tree-wrap">
          <table className="sku-table sku-drilldown-table">
            <thead><tr><th>Lineage</th><th>Caution</th><th className="sku-number">Affected runs</th><th className="sku-number">Frequency</th><th className="sku-number">Events</th><th className="sku-number">Hours lost</th></tr></thead>
            <tbody>{renderNodes(roots)}</tbody>
          </table>
        </div>
      ) : <div className="sku-empty">No Availability evidence exists at this hierarchy level.</div>}
    </>
  );
};

const DynamicDashboard: React.FC<{ payload: RedzoneDynamicPayload }> = ({ payload }) => {
  const { baseline, narrative } = payload;
  const productionGrain = (
    payload.selection.grain === 'sku'
    || payload.selection.grain === 'area'
    || payload.selection.grain === 'location'
  );
  const contextGrainByView: Record<RedzoneDynamicDimension, RedzoneDynamicDimension> = {
    sku: 'location',
    area: 'location',
    location: 'sku',
    problem_type: 'asset_type',
    asset_type: 'asset_name',
    asset_name: 'sku',
  };
  const contributionGrainByView: Record<RedzoneDynamicDimension, RedzoneDynamicDimension> = {
    sku: 'problem_type',
    area: 'sku',
    location: 'problem_type',
    problem_type: 'sku',
    asset_type: 'sku',
    asset_name: 'location',
  };
  const preferredDiagnosticGrain = contextGrainByView[payload.selection.grain];
  const preferredDiagnosticRows = payload.breakdowns[preferredDiagnosticGrain] ?? [];
  const preferredContributionGrain = contributionGrainByView[payload.selection.grain];
  const preferredContributionRows = payload.breakdowns[preferredContributionGrain] ?? [];
  const useContributionForDiagnostics = (
    payload.groups.length <= 1
    && preferredDiagnosticRows.length <= 1
    && preferredContributionRows.length > 1
  );
  const diagnosticGrain = useContributionForDiagnostics
    ? preferredContributionGrain
    : preferredDiagnosticGrain;
  const diagnosticBreakdownRows = useContributionForDiagnostics
    ? preferredContributionRows
    : preferredDiagnosticRows;
  const diagnosticRows = payload.groups.length > 1
    ? payload.groups
    : diagnosticBreakdownRows;
  const diagnosticLabel = payload.groups.length > 1
    ? payload.selection.grain_label
    : DYNAMIC_DIMENSIONS.find(({ key }) => key === diagnosticGrain)?.label
      ?? diagnosticGrain.replaceAll('_', ' ');
  const contextGrain = useContributionForDiagnostics
    ? 'location'
    : preferredContributionGrain;
  const contextRows = payload.breakdowns[contextGrain] ?? [];
  const contextLabel = DYNAMIC_DIMENSIONS.find(({ key }) => key === contextGrain)?.label
    ?? contextGrain.replaceAll('_', ' ');
  const paretoChart = buildDynamicParetoChart(diagnosticRows);
  const recurrenceChart = buildDynamicRecurrenceChart(diagnosticRows);
  const contextTreemap = buildDynamicTreemapChart(contextRows);
  const oeeRows = diagnosticRows.filter((row) => row.average_oee_pct !== null);
  const oeeChart = buildDynamicOeeChart(oeeRows);
  return (
    <div className="dynamic-dashboard">
      <section className="sku-readiness-card">
        <div className="sku-readiness-main">
          <div className="sku-eyebrow"><ClipboardCheck size={14} /> What to check first</div>
          <h2>{narrative.headline}</h2>
          <p>{narrative.summary}</p>
        </div>
        <div className="sku-readiness-action"><strong>Suggested pre-run check</strong><span>{narrative.action}</span></div>
      </section>

      <DynamicSummaryBand payload={payload} />

      <section className="dynamic-diagnostics" aria-labelledby="dynamic-diagnostics-title">
        <div className="dynamic-section-intro">
          <div>
            <div className="sku-eyebrow"><BarChart3 size={14} /> Diagnostic visuals</div>
            <h2 id="dynamic-diagnostics-title">
              {productionGrain
                ? `Performance and loss concentration by ${diagnosticLabel}`
                : `Loss concentration and recurrence by ${diagnosticLabel}`}
            </h2>
          </div>
          <p>
            {productionGrain
              ? 'Production grains lead with OEE versus target; loss evidence follows.'
              : 'Event grains lead with impact and recurrence because OEE cannot be assigned to an event dimension.'}
          </p>
        </div>
        <div className="dynamic-diagnostic-grid">
          <SectionCard
            eyebrow={productionGrain ? 'Performance gap' : 'Impact concentration'}
            title={productionGrain
              ? `Actual versus target OEE by ${diagnosticLabel}`
              : `Pareto of Availability hours by ${diagnosticLabel}`}
            subtitle={productionGrain
              ? 'Ordered from the largest negative OEE gap so the first intervention is visible.'
              : 'Bars show downtime; the line shows how quickly the selected values explain total impact.'}
            icon={productionGrain ? <Gauge size={14} /> : <Timer size={14} />}
            className="sku-chart-panel dynamic-chart-primary"
          >
            {(productionGrain ? oeeRows.length : diagnosticRows.length)
              ? (
                <ReactECharts
                  option={productionGrain ? oeeChart : paretoChart}
                  style={{ height: 350 }}
                  notMerge
                  lazyUpdate
                />
              )
              : <div className="sku-empty">No diagnostic evidence for this selection.</div>}
          </SectionCard>
          <SectionCard
            eyebrow={productionGrain ? 'Loss Pareto' : 'Recurrence map'}
            title={productionGrain
              ? `Where ${diagnosticLabel} losses concentrate`
              : 'Frequency versus hours lost'}
            subtitle={productionGrain
              ? 'The cumulative line separates the vital few values from the long tail.'
              : 'Bubble size represents event volume, separating frequent stops from long-duration losses.'}
            icon={productionGrain ? <Timer size={14} /> : <Route size={14} />}
            className="sku-chart-panel dynamic-chart-secondary"
          >
            {diagnosticRows.length
              ? (
                <ReactECharts
                  option={productionGrain ? paretoChart : recurrenceChart}
                  style={{ height: 350 }}
                  notMerge
                  lazyUpdate
                />
              )
              : <div className="sku-empty">No impact evidence.</div>}
          </SectionCard>
          <SectionCard
            eyebrow={`${contextLabel} contribution`}
            title={`How ${contextLabel} values compose this view`}
            subtitle={`Tile area represents filtered Availability hours within the selected ${payload.selection.grain_label} analysis.`}
            icon={contextGrain === 'sku' ? <Package size={14} /> : <Layers3 size={14} />}
            className="sku-chart-panel dynamic-chart-context"
          >
            {contextRows.some((row) => numberValue(row.total_hours_lost) > 0)
              ? <ReactECharts option={contextTreemap} style={{ height: 300 }} notMerge lazyUpdate />
              : <div className="sku-empty">No {contextLabel} contribution evidence.</div>}
          </SectionCard>
        </div>
      </section>

      <SectionCard
        eyebrow={`Detailed comparison · ${payload.selection.grain_label}`}
        title={`Rank and compare ${payload.selection.grain_label} values`}
        subtitle={payload.methodology.oee_note}
        icon={<BarChart3 size={14} />}
        className="dynamic-results-panel"
      >
        <div className="sku-table-wrap">
          <table className="sku-table dynamic-results-table">
            <thead><tr><th>{payload.selection.grain_label}</th><th>Caution</th><th className="sku-number">Runs</th><th className="sku-number">OEE</th><th className="sku-number">Gap</th><th className="sku-number">Affected</th><th className="sku-number">Frequency</th><th className="sku-number">Events</th><th className="sku-number">Hours lost</th></tr></thead>
            <tbody>{payload.groups.map((row) => (
              <tr key={row.group_key}>
                <td><strong>{row.group_label}</strong></td>
                <td><span className={cautionClass(row.caution_level)}>{row.caution_level}</span></td>
                <td className="sku-number">{formatNumber(row.historical_runs, 0)}</td>
                <td className="sku-number">{formatPercent(row.average_oee_pct, 2)}</td>
                <td className="sku-number">{formatSignedPoints(row.average_oee_delta_pp)}</td>
                <td className="sku-number">{formatNumber(row.affected_runs, 0)}</td>
                <td className="sku-number">{formatPercent(row.frequency_pct, 2)}</td>
                <td className="sku-number">{formatNumber(row.problem_event_count, 0)}</td>
                <td className="sku-number">{formatNumber(row.total_hours_lost, 2)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </SectionCard>

      <section className="dynamic-impact-group" aria-label="Quantified impact and lineage">
        <OverheadImpactPanel payload={payload} />
        <DynamicImpactLineage payload={payload} />
      </section>

      <AiInsightPanel payload={payload} />

      <SectionCard
        eyebrow="Hierarchy drill-down"
        title="Trace the result from production scope to the affected asset"
        subtitle="Area → Location → SKU → Problem Type → Asset Type → Asset Name."
        icon={<Route size={14} />}
        className="sku-drilldown-panel"
      >
        <DynamicHierarchy payload={payload} />
      </SectionCard>
    </div>
  );
};

const AnalysisHeader: React.FC<{
  analysisLevel: RedzonePageMode;
  onLevelChange: (level: RedzonePageMode) => void;
  loading: boolean;
  payload: RedzoneDowntimePayload | RedzoneDynamicPayload | null;
  generatedAt?: string;
  actionSlot?: React.ReactNode;
}> = ({
  analysisLevel,
  onLevelChange,
  loading,
  payload,
  generatedAt,
  actionSlot,
}) => (
  <header className="sku-hero">
    <div className="sku-hero-main">
      <div className="sku-hero-copy">
        <div className="sku-eyebrow"><Factory size={14} /> Redzone Analysis</div>
        <h1>{analysisLevel === 'readiness' ? 'Production readiness' : 'Flexible analysis'}</h1>
        <p>{analysisLevel === 'readiness'
          ? 'Review historical OEE gaps and recurring Availability losses before the next production run.'
          : 'Choose any grain, then constrain the analysis with independent production and asset filters.'}</p>
      </div>
      <div className="sku-hero-controls">
        <AnalysisLevelSwitch value={analysisLevel} onChange={onLevelChange} />
      </div>
    </div>
    <div className="sku-hero-context">
      <span className="sku-scope-status"><ShieldCheck size={15} /> Availability losses only</span>
      <span>Performance and Quality losses are excluded</span>
      <span className="sku-context-spacer" aria-hidden="true" />
      {actionSlot}
      {analysisLevel === 'readiness' && payload ? (
        <>
          <span>Data through <strong>{formatAsOfDate(payload.selection.data_through_date)}</strong></span>
          <span>Analysis last run <strong>{generatedAt ? formatUpdatedAt(generatedAt) : 'Not yet'}</strong></span>
        </>
      ) : null}
    </div>
  </header>
);

const RedzoneDowntimeAnalysisPage: React.FC = () => {
  const [analysisLevel, setAnalysisLevel] = useState<RedzonePageMode>('readiness');
  const [options, setOptions] = useState<RedzoneDowntimeSkuOption[]>([]);
  const [readinessPlant, setReadinessPlant] = useState('');
  const [readinessStartDate, setReadinessStartDate] = useState('');
  const [readinessEndDate, setReadinessEndDate] = useState('');
  const [readinessRefreshJobId, setReadinessRefreshJobId] = useState<string | null>(null);
  const [dynamicFacets, setDynamicFacets] = useState<RedzoneDynamicFacet[]>([]);
  const [dynamicPlant, setDynamicPlant] = useState('');
  const [dynamicGrain, setDynamicGrain] = useState<DynamicViewDimension | ''>('');
  const [dynamicPrimaryValues, setDynamicPrimaryValues] = useState<string[]>([]);
  const [dynamicFilters, setDynamicFilters] = useState<RedzoneDynamicFilters>({});
  const [dynamicStartDate, setDynamicStartDate] = useState('');
  const [dynamicEndDate, setDynamicEndDate] = useState('');
  const [dynamicPlannedMode, setDynamicPlannedMode] = useState<RedzonePlannedMode>('both');
  const [appliedDynamicSelection, setAppliedDynamicSelection] =
    useState<DynamicAnalysisSelection | null>(null);
  const [entrySearch, setEntrySearch] = useState('');
  const [selectedSku, setSelectedSku] = useState('');
  const [skuSearch, setSkuSearch] = useState('');
  const [response, setResponse] = useState<RedzoneDowntimeDashboardResponse | null>(null);
  const [dynamicResponse, setDynamicResponse] = useState<RedzoneDynamicDashboardResponse | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorContext, setErrorContext] = useState<'refresh' | 'view' | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dynamicRefreshJobId, setDynamicRefreshJobId] = useState<string | null>(null);
  const selectedSkuOption = options.find((option) => option.sku === selectedSku);
  const selectedSkuLabel = formatSkuLabel(
    selectedSku,
    selectedSkuOption?.product_type_name,
  );
  const changeAnalysisLevel = (level: RedzonePageMode) => {
    if (level === 'readiness' && analysisLevel !== 'readiness') {
      setResponse(null);
    }
    if (level === 'explore' && analysisLevel !== 'explore') {
      setDynamicPlant('');
      setDynamicGrain('');
      setDynamicPrimaryValues([]);
      setDynamicFilters({});
      setDynamicStartDate('');
      setDynamicEndDate('');
      setDynamicPlannedMode('both');
      setAppliedDynamicSelection(null);
      setDynamicResponse(null);
    }
    setAnalysisLevel(level);
    setError(null);
    setErrorContext(null);
  };

  useEffect(() => {
    let active = true;
    setLoadingOptions(true);
    setError(null);
    setErrorContext(null);
    void redzoneDowntimeApi.listDynamicFacets()
      .then((facetResult) => {
        if (!active) return;
        setDynamicFacets(facetResult.options);
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(apiErrorMessage(cause, 'Unable to load RedZone plant values.'));
          setErrorContext('view');
        }
      })
      .finally(() => { if (active) setLoadingOptions(false); });
    return () => { active = false; };
  }, [refreshKey]);

  useEffect(() => {
    if (analysisLevel !== 'readiness' || !readinessPlant) {
      if (analysisLevel === 'readiness') setOptions([]);
      return undefined;
    }
    let active = true;
    setLoadingOptions(true);
    setError(null);
    setErrorContext(null);
    void redzoneDowntimeApi.listSkuCatalog(readinessPlant)
      .then((result) => {
        if (!active) return;
        setOptions(result.options);
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(apiErrorMessage(cause, 'Unable to load plant-specific SKU values.'));
          setErrorContext('view');
        }
      })
      .finally(() => { if (active) setLoadingOptions(false); });
    return () => { active = false; };
  }, [analysisLevel, readinessPlant]);

  useEffect(() => {
    if (analysisLevel !== 'explore' || !dynamicPlant) return undefined;
    let active = true;
    setLoadingOptions(true);
    setError(null);
    setErrorContext(null);
    const plantRows = dynamicFacets.filter((facet) => facet.dimension === 'plant');
    void redzoneDowntimeApi.listDynamicFacets(dynamicPlant)
      .then((facetResult) => {
        if (!active) return;
        setDynamicFacets([
          ...plantRows,
          ...facetResult.options.filter((facet) => facet.dimension !== 'plant'),
        ]);
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(apiErrorMessage(cause, 'Unable to load plant-specific dropdown values.'));
          setErrorContext('view');
        }
      })
      .finally(() => { if (active) setLoadingOptions(false); });
    return () => { active = false; };
  }, [analysisLevel, dynamicPlant]);

  useEffect(() => {
    if (!dynamicRefreshJobId) return undefined;
    let active = true;
    const poll = async () => {
      try {
        const status = await redzoneDowntimeApi.getDynamicRefreshStatus(dynamicRefreshJobId);
        if (!active) return;
        if (status.status === 'success' && appliedDynamicSelection) {
          const saved = await redzoneDowntimeApi.viewSavedDynamicDashboard({
            plant: appliedDynamicSelection.plant,
            grain: appliedDynamicSelection.grain,
            filters: dynamicRequestFilters(appliedDynamicSelection),
            start_date: appliedDynamicSelection.startDate,
            end_date: appliedDynamicSelection.endDate,
            planned_mode: appliedDynamicSelection.plannedMode,
          });
          if (!active) return;
          if (saved.found && saved.report) {
            setDynamicResponse(saved.report as RedzoneDynamicDashboardResponse);
            setError(null);
            setErrorContext(null);
          }
          setDynamicRefreshJobId(null);
          setLoadingDashboard(false);
        } else if (status.status === 'failed') {
          setError(refreshErrorMessage(status.error_message, 'explore'));
          setErrorContext('refresh');
          setDynamicRefreshJobId(null);
          setLoadingDashboard(false);
        }
      } catch (cause: unknown) {
        if (!active) return;
        setError(apiErrorMessage(cause, 'Unable to check the refresh status.'));
        setErrorContext('refresh');
        setDynamicRefreshJobId(null);
        setLoadingDashboard(false);
      }
    };
    void poll();
    const timer = window.setInterval(() => { void poll(); }, 4000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [dynamicRefreshJobId, appliedDynamicSelection]);

  useEffect(() => {
    if (!readinessRefreshJobId) return undefined;
    let active = true;
    const poll = async () => {
      try {
        const status = await redzoneDowntimeApi.getSkuReadinessRefreshStatus(readinessRefreshJobId);
        if (!active) return;
        if (status.status === 'success') {
          const saved = await redzoneDowntimeApi.viewSavedSkuReadinessDashboard({
            plant: readinessPlant,
            sku: selectedSku,
            start_date: readinessStartDate,
            end_date: readinessEndDate,
          });
          if (!active) return;
          if (saved.found && saved.report) {
            setResponse(saved.report as RedzoneDowntimeDashboardResponse);
            setError(null);
            setErrorContext(null);
          }
          setReadinessRefreshJobId(null);
          setLoadingDashboard(false);
        } else if (status.status === 'failed') {
          setError(refreshErrorMessage(status.error_message, 'readiness'));
          setErrorContext('refresh');
          setReadinessRefreshJobId(null);
          setLoadingDashboard(false);
        }
      } catch (cause: unknown) {
        if (!active) return;
        setError(apiErrorMessage(cause, 'Unable to check the SKU Readiness refresh status.'));
        setErrorContext('refresh');
        setReadinessRefreshJobId(null);
        setLoadingDashboard(false);
      }
    };
    void poll();
    const timer = window.setInterval(() => { void poll(); }, 4000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [readinessRefreshJobId, readinessPlant, selectedSku, readinessStartDate, readinessEndDate]);

  const buildSkuReadinessSavedRequest = () => {
    if (!readinessPlant || !selectedSku || !readinessStartDate || !readinessEndDate) return null;
    return {
      plant: readinessPlant,
      sku: selectedSku,
      start_date: readinessStartDate,
      end_date: readinessEndDate,
    };
  };

  const viewSavedSkuReadinessReport = async () => {
    const request = buildSkuReadinessSavedRequest();
    if (!request || loadingDashboard) return;
    setLoadingDashboard(true);
    setError(null);
    setErrorContext(null);
    try {
      const result = await redzoneDowntimeApi.viewSavedSkuReadinessDashboard(request);
      if (result.found && result.report) {
        setResponse(result.report as RedzoneDowntimeDashboardResponse);
      } else {
        setResponse(null);
        setError(result.message || 'No saved report found. Please try Refresh.');
        setErrorContext('view');
      }
    } catch (cause: unknown) {
      setError(apiErrorMessage(cause, 'Unable to load the saved SKU Readiness report.'));
      setErrorContext('view');
    } finally {
      setLoadingDashboard(false);
    }
  };

  const refreshSkuReadinessReport = async () => {
    const request = buildSkuReadinessSavedRequest();
    if (!request || loadingDashboard) return;
    setLoadingDashboard(true);
    setError(null);
    setErrorContext(null);
    try {
      const result = await redzoneDowntimeApi.refreshSkuReadinessDashboard(request);
      setReadinessRefreshJobId(result.pipeline_run_id);
    } catch (cause: unknown) {
      setError(apiErrorMessage(cause, 'Unable to start the SKU Readiness refresh job.'));
      setErrorContext('refresh');
      setLoadingDashboard(false);
    }
  };

  const buildDynamicSavedRequest = () => {
    if (!dynamicPlant || !dynamicGrain || !dynamicStartDate || !dynamicEndDate) return null;
    const selection: DynamicAnalysisSelection = {
      plant: dynamicPlant,
      grain: dynamicGrain,
      primaryValues: dynamicPrimaryValues,
      filters: dynamicFilters,
      startDate: dynamicStartDate,
      endDate: dynamicEndDate,
      plannedMode: dynamicPlannedMode,
    };
    return {
      selection,
      request: {
        plant: selection.plant,
        grain: selection.grain,
        filters: dynamicRequestFilters(selection),
        start_date: selection.startDate,
        end_date: selection.endDate,
        planned_mode: selection.plannedMode,
      },
    };
  };

  const viewSavedDynamicReport = async () => {
    const built = buildDynamicSavedRequest();
    if (!built || loadingDashboard) return;
    setAppliedDynamicSelection(built.selection);
    setLoadingDashboard(true);
    setError(null);
    setErrorContext(null);
    try {
      const result = await redzoneDowntimeApi.viewSavedDynamicDashboard(built.request);
      if (result.found && result.report) {
        setDynamicResponse(result.report as RedzoneDynamicDashboardResponse);
      } else {
        setDynamicResponse(null);
        setError(result.message || 'No saved report found. Please try Refresh.');
        setErrorContext('view');
      }
    } catch (cause: unknown) {
      setError(apiErrorMessage(cause, 'Unable to load the saved RedZone report.'));
      setErrorContext('view');
    } finally {
      setLoadingDashboard(false);
    }
  };

  const refreshDynamicReport = async () => {
    const built = buildDynamicSavedRequest();
    if (!built || loadingDashboard) return;
    setAppliedDynamicSelection(built.selection);
    setLoadingDashboard(true);
    setError(null);
    setErrorContext(null);
    try {
      const result = await redzoneDowntimeApi.refreshDynamicDashboard(built.request);
      setDynamicRefreshJobId(result.pipeline_run_id);
    } catch (cause: unknown) {
      setError(apiErrorMessage(cause, 'Unable to start the RedZone refresh job.'));
      setErrorContext('refresh');
      setLoadingDashboard(false);
    }
  };

  const responseSku = response?.payload.selection.sku?.trim().toLocaleLowerCase();
  const plantOptions = aggregateDynamicFacets(dynamicFacets.filter((facet) => facet.dimension === 'plant'));
  const payload = (
    responseSku === selectedSku.trim().toLocaleLowerCase()
    && response?.payload.selection.plant === readinessPlant
    && savedDateRangeMatches(response?.payload.selection.date_range, readinessStartDate, readinessEndDate)
  )
    ? response?.payload ?? null
    : null;
  const readinessCanRun = Boolean(
    readinessPlant && selectedSku && readinessStartDate && readinessEndDate,
  );

  if (loadingOptions && !options.length && !dynamicFacets.length) {
    return <div className="sku-caution-shell finance-v2-repo2-theme"><div className="sku-caution-width"><div className="sku-loading">Loading live RedZone downtime history…</div></div></div>;
  }

  if (analysisLevel === 'readiness' && !payload) {
    return (
      <div className="sku-caution-shell finance-v2-repo2-theme">
        <div className="sku-caution-width">
          <AnalysisHeader
            analysisLevel={analysisLevel}
            onLevelChange={changeAnalysisLevel}
            loading={loadingDashboard || loadingOptions}
            payload={null}
            actionSlot={(
              <AnalysisActionButtons
                canRun={readinessCanRun}
                loading={loadingDashboard || loadingOptions}
                onViewSaved={viewSavedSkuReadinessReport}
                onRefreshReport={refreshSkuReadinessReport}
              />
            )}
          />
          <SkuReadinessControls
            plant={readinessPlant}
            sku={selectedSku}
            skuOptions={options}
            plantOptions={plantOptions}
            skuSearch={entrySearch}
            startDate={readinessStartDate}
            endDate={readinessEndDate}
            loading={loadingDashboard || loadingOptions}
            hasReport={false}
            onPlantChange={(plant) => {
              setReadinessPlant(plant);
              setSelectedSku('');
              setOptions([]);
              setResponse(null);
              setError(null);
            }}
            onSkuChange={(sku) => {
              setSelectedSku(sku);
              setResponse(null);
              setError(null);
            }}
            onSkuSearch={setEntrySearch}
            onDateRangeChange={(startDate, endDate) => {
              setReadinessStartDate(startDate);
              setReadinessEndDate(endDate);
              setResponse(null);
              setError(null);
            }}
            onViewSaved={viewSavedSkuReadinessReport}
            onRefreshReport={refreshSkuReadinessReport}
          />
          {loadingOptions && !loadingDashboard && (
            <div className="dynamic-options-loading" role="status" aria-live="polite">
              <RefreshCw className="is-spinning" size={18} />
              <strong>Loading dropdown values</strong>
              <span>{readinessPlant ? 'Fetching plant-specific SKU values.' : 'Fetching available plants.'}</span>
            </div>
          )}
          {loadingDashboard ? (
            <div className="sku-analysis-loading"><RefreshCw className="is-spinning" size={20} /><strong>Refreshing {selectedSkuLabel || 'SKU Readiness'}</strong><span>The report will load when the background job completes.</span></div>
          ) : (
            <div className={`dynamic-awaiting-selection${error ? ' is-error' : ''}`}>
              {error ? <AlertTriangle size={20} /> : <BarChart3 size={20} />}
              <strong>{error || 'Choose plant, SKU, and date range'}</strong>
              {errorContext === 'refresh' && (
                <span>Try a different date range, SKU, or plant, then refresh the analysis again.</span>
              )}
              {!error && <span>Then view a saved report or refresh SAP data in the background.</span>}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (analysisLevel === 'explore') {
    const appliedFilters = appliedDynamicSelection
      ? dynamicRequestFilters(appliedDynamicSelection)
      : {};
    const dynamicPayload = (
      appliedDynamicSelection
      && dynamicResponse?.payload.selection.plant === appliedDynamicSelection.plant
      && dynamicResponse?.payload.selection.grain === appliedDynamicSelection.grain
      && dynamicFilterSignature(dynamicResponse.payload.selection.filters)
        === dynamicFilterSignature(appliedFilters)
      && savedDateRangeMatches(dynamicResponse.payload.selection.date_range, appliedDynamicSelection.startDate, appliedDynamicSelection.endDate)
      && (dynamicResponse.payload.selection.planned_mode ?? 'unplanned') === appliedDynamicSelection.plannedMode
    ) ? dynamicResponse.payload : null;
    const draftMatchesApplied = Boolean(
      appliedDynamicSelection
      && dynamicPlant === appliedDynamicSelection.plant
      && dynamicGrain === appliedDynamicSelection.grain
      && dynamicFilterSignature({ value: dynamicPrimaryValues })
        === dynamicFilterSignature({ value: appliedDynamicSelection.primaryValues })
      && dynamicFilterSignature(dynamicFilters)
        === dynamicFilterSignature(appliedDynamicSelection.filters)
      && dynamicStartDate === appliedDynamicSelection.startDate
      && dynamicEndDate === appliedDynamicSelection.endDate
      && dynamicPlannedMode === appliedDynamicSelection.plannedMode
    );
    const dynamicCanRun = Boolean(dynamicPlant && dynamicGrain && dynamicStartDate && dynamicEndDate);
    return (
      <div className="sku-caution-shell finance-v2-repo2-theme">
        <div className="sku-caution-width">
          <AnalysisHeader
            analysisLevel={analysisLevel}
            onLevelChange={changeAnalysisLevel}
            loading={loadingDashboard || loadingOptions}
            payload={dynamicPayload}
            generatedAt={dynamicResponse?.generated_at}
            actionSlot={(
              <AnalysisActionButtons
                canRun={dynamicCanRun}
                loading={loadingDashboard || loadingOptions}
                current={Boolean(dynamicResponse) && draftMatchesApplied}
                onViewSaved={viewSavedDynamicReport}
                onRefreshReport={refreshDynamicReport}
              />
            )}
          />
          <DynamicAnalysisBuilder
            plant={dynamicPlant}
            grain={dynamicGrain}
            primaryValues={dynamicPrimaryValues}
            onPlantChange={(plant) => {
              setDynamicPlant(plant);
              setDynamicGrain('');
              setDynamicPrimaryValues([]);
              setDynamicFilters({});
              setAppliedDynamicSelection(null);
              setDynamicResponse(null);
              setError(null);
            }}
            onGrainChange={(grain) => {
              setDynamicGrain(grain);
              setDynamicPrimaryValues(grain ? [DYNAMIC_ALL_VALUES_TOKEN] : []);
              setDynamicFilters({});
              setError(null);
            }}
            onPrimaryValuesChange={(values) => {
              setDynamicPrimaryValues(values);
              setDynamicFilters({});
              setError(null);
            }}
            facets={dynamicFacets}
            filters={dynamicFilters}
            onFiltersChange={setDynamicFilters}
            startDate={dynamicStartDate}
            endDate={dynamicEndDate}
            plannedMode={dynamicPlannedMode}
            onDateRangeChange={(startDate, endDate) => {
              setDynamicStartDate(startDate);
              setDynamicEndDate(endDate);
              setError(null);
            }}
            onPlannedModeChange={(mode) => {
              setDynamicPlannedMode(mode);
              setError(null);
            }}
            hasReport={Boolean(dynamicResponse)}
            isDirty={!draftMatchesApplied}
            onViewSaved={viewSavedDynamicReport}
            onRefreshReport={refreshDynamicReport}
            loading={loadingDashboard}
          />
          {loadingOptions && !loadingDashboard && (
            <div className="dynamic-options-loading" role="status" aria-live="polite">
              <RefreshCw className="is-spinning" size={18} />
              <strong>Loading filter values</strong>
              <span>{dynamicPlant ? 'Fetching plant-specific dropdown values.' : 'Fetching available plants.'}</span>
            </div>
          )}
          {loadingDashboard && !dynamicPayload ? (
            <div className="sku-analysis-loading"><RefreshCw className="is-spinning" size={20} /><strong>Building analysis by {DYNAMIC_DIMENSIONS.find((item) => item.key === appliedDynamicSelection?.grain)?.label}</strong><span>Applying the selected value and optional filters.</span></div>
          ) : dynamicPayload ? <DynamicDashboard payload={dynamicPayload} /> : (
            <div className={`dynamic-awaiting-selection${error ? ' is-error' : ''}`}>
              {error ? <AlertTriangle size={20} /> : <BarChart3 size={20} />}
              <strong>{error || 'Choose plant, date range, and analysis grain'}</strong>
              {errorContext === 'refresh' && (
                <span>Try a different date range or filter combination, then refresh the analysis again.</span>
              )}
              {!error && <span>Then view a saved report or refresh SAP data in the background.</span>}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!payload) return null;
  const { baseline, narrative } = payload;
  const topProblems = payload.problems;
  const areaChart = buildAreaChart(payload.areas);
  const hoursChart = buildProblemHoursChart(topProblems);
  const frequencyChart = buildFrequencyChart(payload.locations);

  return (
    <div className="sku-caution-shell finance-v2-repo2-theme">
      <div className="sku-caution-width">
        <AnalysisHeader
          analysisLevel={analysisLevel}
          onLevelChange={changeAnalysisLevel}
          loading={loadingDashboard || loadingOptions}
          payload={payload}
          generatedAt={response?.generated_at}
          actionSlot={(
            <AnalysisActionButtons
              canRun={readinessCanRun}
              loading={loadingDashboard || loadingOptions}
              current={Boolean(payload)}
              onViewSaved={viewSavedSkuReadinessReport}
              onRefreshReport={refreshSkuReadinessReport}
            />
          )}
        />

        <SkuReadinessControls
          plant={readinessPlant}
          sku={selectedSku}
          skuOptions={options}
          plantOptions={plantOptions}
          skuSearch={skuSearch}
          startDate={readinessStartDate}
          endDate={readinessEndDate}
          loading={loadingDashboard || loadingOptions}
          hasReport={Boolean(response)}
          onPlantChange={(plant) => {
            setReadinessPlant(plant);
            setSelectedSku('');
            setOptions([]);
            setResponse(null);
            setError(null);
          }}
          onSkuChange={(sku) => {
            setSelectedSku(sku);
            setResponse(null);
            setError(null);
          }}
          onSkuSearch={setSkuSearch}
          onDateRangeChange={(startDate, endDate) => {
            setReadinessStartDate(startDate);
            setReadinessEndDate(endDate);
            setResponse(null);
            setError(null);
          }}
          onViewSaved={viewSavedSkuReadinessReport}
          onRefreshReport={refreshSkuReadinessReport}
        />

        {error && <div className="sku-inline-error"><AlertTriangle size={15} />{error}</div>}

        <section className="sku-readiness-card">
          <div className="sku-readiness-main">
            <div className="sku-eyebrow"><ClipboardCheck size={14} /> What to check first</div>
            <h2>{narrative.headline}</h2>
            <p>{narrative.summary}</p>
          </div>
          <div className="sku-readiness-action"><strong>Suggested pre-run check</strong><span>{narrative.action}</span></div>
        </section>

        <OverheadImpactPanel payload={payload} />

        <ImpactHighlights payload={payload} />

        <div className="sku-kpi-grid">
          <KpiCard label="Average OEE" value={formatPercent(baseline.average_oee_pct, 2)} detail="Actual historical average" tone="teal" icon={<Gauge size={15} />} />
          <KpiCard label="Target OEE" value={formatPercent(baseline.average_target_oee_pct, 2)} detail="Target historical average" tone="blue" icon={<Gauge size={15} />} />
          <KpiCard label="OEE gap to target" value={formatSignedPoints(baseline.average_oee_delta_pp)} detail={`Actual minus target · ${baseline.average_target_oee_pct ? formatPercent((numberValue(baseline.average_oee_delta_pp) / numberValue(baseline.average_target_oee_pct)) * 100, 1) : '—'} relative`} tone="red" icon={<BarChart3 size={15} />} />
          <KpiCard label="Total Runs" value={formatNumber(baseline.historical_runs, 0)} detail={`${formatNumber(baseline.historical_run_uuid_segments, 0)} total line runs`} tone="navy" icon={<Route size={15} />} />
          <KpiCard label="Average run time" value={`${formatNumber(baseline.average_run_time_minutes, 1)} min`} detail={`${formatNumber(baseline.area_count, 0)} production areas · Run ID basis`} tone="blue" icon={<Timer size={15} />} />
          <KpiCard label="Availability events" value={formatNumber(baseline.availability_problem_events, 0)} detail={`${baseline.historical_runs ? formatNumber(baseline.availability_problem_events / baseline.historical_runs, 2) : '—'} per Run ID · ${formatNumber(baseline.availability_problem_groups, 0)} groups`} tone="amber" icon={<AlertTriangle size={15} />} />
          <KpiCard label="Availability hours lost" value={`${formatNumber(baseline.availability_hours_lost, 2)} h`} detail={`${baseline.historical_runs ? formatNumber(baseline.availability_hours_lost / baseline.historical_runs, 2) : '—'} h per Run ID`} tone="red" icon={<Factory size={15} />} />
        </div>

        <div className="sku-chart-grid">
          <SectionCard eyebrow="Area performance" title="Where the OEE gap is visible" subtitle="Actual and target OEE by area. One Run ID may appear in multiple area/location segments." icon={<BarChart3 size={14} />} className="sku-chart-panel sku-chart-panel-wide">
            {payload.areas.length ? <ReactECharts option={areaChart} style={{ height: Math.max(230, payload.areas.length * 42) }} notMerge lazyUpdate /> : <div className="sku-empty">No area segments in scope.</div>}
          </SectionCard>
          <SectionCard eyebrow="Impact ranking" title="Top Availability problem locations by hours lost" subtitle="Recorded hours are evidence of impact, not proof of causation." icon={<Timer size={14} />} className="sku-chart-panel">
            {topProblems.length ? <ReactECharts option={hoursChart} style={{ height: 320 }} notMerge lazyUpdate /> : <div className="sku-empty">No Availability problem events were linked.</div>}
          </SectionCard>
          <SectionCard eyebrow="Recurrence" title="Locations with the most affected runs" subtitle="Frequency is affected Run IDs divided by the historical Run ID denominator for that location." icon={<Route size={14} />} className="sku-chart-panel">
            {payload.locations.some((row) => numberValue(row.affected_runs) > 0) ? <ReactECharts option={frequencyChart} style={{ height: 320 }} notMerge lazyUpdate /> : <div className="sku-empty">No recurring Availability locations were found.</div>}
          </SectionCard>
        </div>

        <AiInsightPanel payload={payload} />

        <SectionCard eyebrow="Hierarchy drill-down" title="Follow the Availability problem lineage" subtitle="Choose where to start, then expand each parent to see its affected children." icon={<Route size={14} />} className="sku-drilldown-panel">
          <DrilldownExplorer levels={payload.hierarchy_levels} rows={payload.drilldowns} />
        </SectionCard>

      </div>
    </div>
  );
};

export default RedzoneDowntimeAnalysisPage;
